/* eslint-disable no-unused-vars */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-useless-return */
/* eslint-disable no-console */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { isEmpty } from 'lodash';
import { leadFlatFields, leadTableFields } from 'src/constants/fields';
import { parseV2ToV1 } from 'src/utils/fieldUtils';
import {
    mergeAdAttributionIntoLeadPayload,
    clearStoredAdAttribution,
} from 'src/utils/adAttribution';
import { practiceInSuffix } from 'src/utils/practiceInSuffix';
import { buildBookedWidgetDateQuery } from 'src/utils/bookedTableFilters';
import { buildManualLeadPayload, getCreatedManualLeadTreatment } from 'src/utils/buildManualLeadPayload';
import { captureAdvancedLeadCreate, isSeparatelyRequestedFutureTreatmentBooking } from 'src/utils/attendanceActions.ts';
import { postManualLead, isIdentityConflict } from 'src/utils/leadIdentityApi';
import { friendlyCreateLeadError } from 'src/utils/createLeadErrors';
import { buildHealthcareProviderFilter, normalizeLeadTableQuery } from 'src/utils/leadTableQuery';
import { extractApiError } from 'src/utils/extractApiError';
import { withAssigneeIds } from 'src/utils/toAssigneeIds';
import { invalidateAttendance } from '../attendanceReview.ts';
import { dispatch, store } from '../../store';
import { resolveRequestBucket } from '../../../utils/newLeadDueNowCompat';
import { updateError } from '../user';
import { callApi } from '../../../utils/apiCall';
import { phoneSaveError, withStrictPhoneCheck } from '../../../utils/leadPhone';
import { hasFeatureAccess } from '../../../utils/featurePermission';
import { errorHandler } from '../../../utils/errorHandler';
import { localStorageRemoveItem } from '../../../utils/storage-available';
import { getCurrentTreatmentIdV2 } from '../clincheck';
import { setLoader } from '../loader';
import {
    leadActions,
    startLoadingData,
    updateCompleteStatus,
    updateDashNewLeadsTable,
    archiveLead
} from './slice';

import { StartingTreatment, addTreatment, continueCreatedTreatment, editTreatment, UpdateTreatmentV2 } from './treatmentThunks';
import { addAppointment, getAppointment, followUpSlice, unableToContact } from './appointmentThunks';
import { addReminder, updateAssignToUser } from './taskThunks';
import { leadTreatmentPayment } from './financialThunks';

dayjs.extend(utc);
dayjs.extend(timezone);

// ── Utility functions ────────────────────────────────────────

export const getDepositPendingStatus = (status) => status === 'NO';

// Monotonic token for DashNewLeadTable list calls. Guards against last-writer-wins
// corruption from racing bleed requests on cold load: each getNewLead dispatch
// increments this counter and captures the value; the success handler discards
// any response whose token is no longer the latest.
let _dashNewLeadToken = 0;

// ConsultantAttend writes every tab/filter/practice request into one shared
// Redux table. A single monotonic generation plus the selected-practice
// boundary prevents an older response from replacing the latest visit. Local
// resultOnly consumers are deliberately excluded so count probes cannot
// supersede the visible list request.
let _consultantAttendToken = 0;
let _latestConsultantAttendBoundaryKey = null;

const consultantAttendBoundaryKey = (practiceId, activePracticeIds) => (
    `${practiceId == null ? '' : String(practiceId)}|${practiceInSuffix(activePracticeIds)}`
);

const beginConsultantAttendRequest = (practiceId, activePracticeIds) => {
    _consultantAttendToken += 1;
    _latestConsultantAttendBoundaryKey = consultantAttendBoundaryKey(practiceId, activePracticeIds);
    return {
        practiceKey: String(practiceId),
        token: _consultantAttendToken,
        boundaryKey: _latestConsultantAttendBoundaryKey,
    };
};

const isLatestConsultantAttendRequest = (request) => {
    if (request == null) return true;
    const livePracticeState = store.getState().practiceTreatmentList || {};
    if (livePracticeState.practiceId == null) return false;
    const livePracticeKey = String(livePracticeState.practiceId);
    const liveBoundaryKey = consultantAttendBoundaryKey(
        livePracticeKey,
        livePracticeState.activePracticeIds,
    );
    return (
        request.token === _consultantAttendToken
        && request.boundaryKey === _latestConsultantAttendBoundaryKey
        && request.boundaryKey === liveBoundaryKey
    );
};

// ConsultantBooked writes into one shared Redux table. Keep request generations
// per practice and also remember which practice issued the newest table request,
// so late responses cannot overwrite a newer filter or practice selection.
const _consultantBookedTokens = new Map();
let _latestConsultantBookedPractice = null;
let _consultantBookedBoundaryKey = null;
let _consultantBookedBoundaryGeneration = 0;

const consultantBookedBoundaryKey = (practiceId, activePracticeIds) => (
    `${practiceId == null ? '' : String(practiceId)}|${practiceInSuffix(activePracticeIds)}`
);

export const setConsultantBookedRequestBoundary = (practiceId, activePracticeIds = null) => {
    const nextBoundaryKey = consultantBookedBoundaryKey(practiceId, activePracticeIds);
    if (nextBoundaryKey === _consultantBookedBoundaryKey) return;
    _consultantBookedBoundaryKey = nextBoundaryKey;
    _consultantBookedBoundaryGeneration += 1;
    _latestConsultantBookedPractice = practiceId == null ? null : String(practiceId);
};

const beginConsultantBookedRequest = (practiceId, activePracticeIds) => {
    setConsultantBookedRequestBoundary(practiceId, activePracticeIds);
    const practiceKey = String(practiceId);
    const token = (_consultantBookedTokens.get(practiceKey) || 0) + 1;
    _consultantBookedTokens.set(practiceKey, token);
    _latestConsultantBookedPractice = practiceKey;
    return {
        practiceKey,
        token,
        boundaryKey: _consultantBookedBoundaryKey,
        boundaryGeneration: _consultantBookedBoundaryGeneration,
    };
};

const isLatestConsultantBookedRequest = (request) => {
    if (request == null) return true;
    const livePracticeState = store.getState().practiceTreatmentList || {};
    const livePracticeKey = livePracticeState.practiceId == null
        ? request.practiceKey
        : String(livePracticeState.practiceId);
    const liveBoundaryKey = consultantBookedBoundaryKey(
        livePracticeKey,
        livePracticeState.activePracticeIds,
    );
    return (
        request.practiceKey === _latestConsultantBookedPractice
        && _consultantBookedTokens.get(request.practiceKey) === request.token
        && request.boundaryKey === _consultantBookedBoundaryKey
        && request.boundaryGeneration === _consultantBookedBoundaryGeneration
        && request.boundaryKey === liveBoundaryKey
    );
};

// Builds the query-string suffix that scopes a request to a custom treatment tab.
// Delegates all include/exclude/sibling resolution to the backend via ?custom_table_tab=<id>.
// The backend resolves the tab by ID + practice, calls _build_custom_tab_q(), and applies the
// correct Q filter — covering ad-hoc excludes, sibling-tab excludes, and attribute predicates.
// The isV2Flat parameter is kept for signature compatibility but is no longer needed:
// the backend handles both V1 and V2-flat endpoints with the same param.
export const buildCustomTabQuery = (practiceId, customTabId, { isV2Flat = false } = {}) => {
    if (!customTabId) return '';
    return `&custom_table_tab=${encodeURIComponent(customTabId)}`;
};

export const getCurrentLeadAppointmentId = (id) => {
    const { currentTreatmentDataGlobal } = store.getState().multiTreatment;
    if (!isEmpty(currentTreatmentDataGlobal?.treatmentData?.appointments)) return currentTreatmentDataGlobal?.treatmentData?.appointments?.[0]?.id;
    return id;
};

// TIMING CONTRACT: must stay > useTaskChecklistColumns' 150 ms debounce
// (src/hooks/useTaskChecklistColumns.js), or NewLeadDataGrid's checklist-switch
// gate drops before the cell fetch starts → blank-cell jank returns.
// Cross-ref: NewLeadDataGrid.jsx clearing effect (LEAD_LOADING_SETTLE_MS).
const LEAD_LOADING_SETTLE_MS = 500;

const handleLoading = (type, state, isCurrent = () => true) => {
    if (!isCurrent()) return;
    if (type === 'ConsultantClinCheckAttend') {
        if (state === false) {
            setTimeout(() => {
                if (isCurrent()) dispatch(leadActions.setLoadingForClincheckLead(state));
            }, LEAD_LOADING_SETTLE_MS);
            return;
        }
        dispatch(leadActions.setLoadingForClincheckLead(state));
    } else if (state === false) {
        setTimeout(() => {
            if (isCurrent()) dispatch(leadActions.setLoadingForLead(state));
        }, LEAD_LOADING_SETTLE_MS);
    } else {
        dispatch(leadActions.setLoadingForLead(state));
    }
};

const getQuery = (data) => {
    let query = '';
    if (data?.isNoTaskTable) {
        // V2-aware filter: excludes leads with an open V1 Reminder OR an open V2
        // checklist task. Leads whose checklist is fully completed still appear —
        // they need a next action. Degrades gracefully on V1-only practices.
        query += '&has_no_open_followup=true';
    }
    return query;
};

export const isLeadTreatmentFlatTablePath = (filterStatus, type, forceV1API) => (
    !forceV1API
    && (
        filterStatus === 'TREATMENT_STARTED'
        || (filterStatus === 'CONSULTATION_ATTENDED' && type === 'ConsultantAttend')
    )
);

// ── Follow-up table threshold helper ─────────────────────────
// Tables: New (0), Follow up 1 (1), Follow up 2 (2), Follow up 3+ (3+)
function getFollowUpTable(count) {
    if (count <= 0) return 0;
    if (count === 1) return 1;
    if (count === 2) return 2;
    return 3;
}
const FOLLOW_UP_TABLE_LABELS = ['New', 'Follow up 1', 'Follow up 2', 'Follow up 3+'];

function checkFollowUpTableMove(leadState, treatmentId) {
    const lead = leadState?.followLead || leadState?.tableRowLead;
    if (!lead) return null;

    const treatments = lead?.lead_treatments || [];
    const treatment = treatmentId
        ? treatments.find((t) => t.id === treatmentId)
        : treatments[0];
    if (!treatment) return null;

    // Only counts for matching status (NEW) are relevant
    const counts = treatment?.lead_contacted_counts || [];
    const countObj = counts.find((c) => c.status === (treatment?.status || 'NEW')) || counts[0];
    const oldCount = countObj?.count ?? 0;
    const newCount = oldCount + 1;

    const oldTable = getFollowUpTable(oldCount);
    const newTable = getFollowUpTable(newCount);

    if (newTable !== oldTable) {
        const firstName = lead?.first_name || '';
        const lastName = lead?.last_name || '';
        return {
            leadId: lead?.id || lead?.lead_id,
            leadName: `${firstName} ${lastName}`.trim() || 'Lead',
            oldTable,
            newTable,
            oldLabel: FOLLOW_UP_TABLE_LABELS[oldTable],
            newLabel: FOLLOW_UP_TABLE_LABELS[newTable],
            treatmentName: treatment?.treatment?.name || treatment?.treatment_name || '',
        };
    }
    return null;
}

// ── Table data fetching ──────────────────────────────────────

// Keep all New Lead table and search requests on the same lightweight field set.
// leadTableFields now intentionally lists overdue_task_count/next_task_due_at/
// next_task_title (2026-09-30, Next Task column) — no stripping here.
const searchSafeTableFields = () => leadTableFields;

// Count-only (resultOnly) probes only read `count` — ask for the minimal row shape instead of the full lead field set
// (same shape getAwaitingLead's countOnly uses). ~2x faster server-side on big practices.
const COUNT_ONLY_FIELDS = 'fields={id}';

// 'due_now' → capability-aware bucket to send (see utils/newLeadDueNowCompat).
async function resolveTaskDueBucketForRequest(bucket, practiceId, searchQuery) {
    if (bucket !== 'due_now') return bucket;
    let counts = store.getState().widgetCounter?.newLeadTaskCounts ?? null;
    if (!counts && !(searchQuery && searchQuery.length > 0)) {
        try {
            const res = await callApi(`/api/v1/practices/${practiceId}/new-lead-task-widget-counts/`);
            if (res?.status === 200 && res.data && typeof res.data === 'object') counts = res.data;
        } catch (err) {
            // Capability unknown → keep 'due_now' (new-backend behaviour).
        }
    }
    return resolveRequestBucket(bucket, counts);
}

export const getNewLead = ({ id, limit: rawLimit, offset: rawOffset, filterStatus, order, orderId, searchQuery, treatment, startDate, endDate, contactedCount, tags, referral, type, category, bookedWith, multiStatus, depositPendingStatus, apptType, consultationBookedWith, isNoTaskTable, currentTab, isArchived, isCacheApi, nhsProviderStatus, assigneeIds, forceV1API, isAnalyticsEnabled, noTreatment, acrossPractice, practices, leadIdsToSearch, bookedWidgetFilter, directCountFilter, customTabId, checklistState, forceRefetch, tasksDueNow, noNextAction, taskDueBucket, tasksDueBucket, activePracticeIds, resultOnly = false, priority = undefined }) =>
    async () => {
        // Strip undefined pagination params to prevent "limit=undefined" in URL
        const limit = rawLimit != null && !Number.isNaN(Number(rawLimit)) ? rawLimit : undefined;
        const offset = rawOffset != null && !Number.isNaN(Number(rawOffset)) ? rawOffset : undefined;
        if (limit === undefined || offset === undefined) {
            // eslint-disable-next-line no-console
            console.warn('[getNewLead] skipped: limit/offset undefined', { practiceId: id, type, limit, offset });
            return;
        }
        // Capture a monotonic token at dispatch time. DashNewLeadTable success handler
        // uses this to discard responses from any call that was superseded by a newer dispatch.
        if (type === 'DashNewLeadTable') _dashNewLeadToken += 1;
        const myToken = type === 'DashNewLeadTable' ? _dashNewLeadToken : 0;
        const requestActivePracticeIds = activePracticeIds !== undefined
            ? activePracticeIds
            : store.getState().practiceTreatmentList?.activePracticeIds;
        const consultantBookedRequest = type === 'ConsultantBooked' && !resultOnly
            ? beginConsultantBookedRequest(id, requestActivePracticeIds)
            : null;
        const consultantAttendRequest = type === 'ConsultantAttend' && !resultOnly
            ? beginConsultantAttendRequest(id, requestActivePracticeIds)
            : null;
        const isCurrentRequest = () => (
            isLatestConsultantBookedRequest(consultantBookedRequest)
            && isLatestConsultantAttendRequest(consultantAttendRequest)
        );
        if (!isAnalyticsEnabled) {
            try {
                if (!resultOnly) handleLoading(type, true, isCurrentRequest);
                // The mounted ConsultantAttend route renders this state as an
                // alert. Clear a prior error only when beginning its next
                // request; stale failures remain guarded by isCurrentRequest.
                if (type === 'ConsultantAttend' && !resultOnly) {
                    dispatch(leadActions.clearError());
                }
                // New-Lead "Tasks / Due" bucketed mode: when a task_due_bucket is set the BE
                // applies the canonical NEW base itself (is_archived=False, is_spam=False,
                // Exists(LeadTreatment status=NEW), all providers) so the card count == table
                // == rows. The FE still sends `lead_treatments__status=NEW` (see queryParams) as a
                // safety net, but must NOT layer on the contacts-mode divergence (has_new_or_null_treatment
                // and healthcare_provider=PRIVATE), which scoped to a different population than
                // the widget-count endpoint. Scope is bucketed tasks-mode ONLY — contacts mode
                // and every other table type stay byte-identical.
                // Backend-capability compat (see utils/newLeadDueNowCompat): on an OLD backend
                // (counts have no `due_now`) the 'due_now' bucket is sent as 'due_today' (the
                // superset). If the counts aren't in the store yet we ask for them here — the same
                // GET the widget issues, so callApi's GET dedupe joins it (no extra request, and no
                // double table fetch): the table request simply waits for the capability.
                const effectiveTaskDueBucket = await resolveTaskDueBucketForRequest(taskDueBucket, id, searchQuery);
                const isBucketedTasksMode = !!effectiveTaskDueBucket;
                // Search active in tasks-mode: lift the bucket filter and revert to
                // contacts-mode query shape so ALL matching NEW leads are returned.
                // Computed early so healthCareProvider and newLeadBaseParam can use it.
                const hasSearch = !!(searchQuery && searchQuery.length > 0);
                // ── Bucket-owned scope ────────────────────────────────────────
                // TRUE only when the BE is the one applying the NEW-lead base: a bucket is
                // active AND no search is running. Sending `task_due_bucket` makes the BE run
                // apply_new_lead_base_predicates (is_archived=False, is_spam=False,
                // Exists(LeadTreatment status=NEW), NHS excluded), so the FE must not
                // duplicate that scoping.
                //
                // A search lifts the bucket, which ends that arrangement: the FE stops
                // sending task_due_bucket, and the BE then applies NO base of its own — the
                // /leads/search/ action (apps/leads/views.py) runs only filter_queryset()
                // over whatever params arrive. From that point the FE owns the NEW-stage scope.
                //
                // EVERY consumer that means "the BE owns the base" MUST read this constant,
                // never the raw isBucketedTasksMode. RCA 2026-09-06: commit 498cc2ed3 gated
                // three of the four consumers on !hasSearch and missed `queryParams`, so a
                // tasks-mode search went up with NO stage filter at all and returned booked /
                // attended / closed leads on the New tab (lead 190647, practice 11).
                const isBucketOwnedScope = isBucketedTasksMode && !hasSearch;
                // Determine effective date and extra query based on bookedWidgetFilter.
                const effectiveFilterStatus = filterStatus;
                const isV2FlatPath = isLeadTreatmentFlatTablePath(effectiveFilterStatus, type, forceV1API);
                // Hardcoded: NHS leads are handled on the dedicated NHS tab (getNHSLeads /
                // nhsProviderStatus→healthcare_provider=NHS). Exclude them from the main
                // new-lead table in ALL modes (bucketed tasks AND contacts) by sending
                // healthcare_provider__in=PRIVATE,OTHER — keeps Private + Other, drops only NHS.
                let healthCareProviderQuery = '';
                const healthCareProvider = buildHealthcareProviderFilter({
                    isFlatPath: isV2FlatPath,
                    isArchived,
                    nhsProviderStatus,
                });
                if (nhsProviderStatus) {
                    healthCareProviderQuery = nhsProviderStatus === "WAITING" ? '&lead_note__note__in=NHS_WAITING_LIST' : nhsProviderStatus === 'NONWAITING' ? '&lead_note__note__not_in=NHS_WAITING_LIST' : '&is_registered_patient=true';
                }
                const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;

                let widgetDateOverride = '';
                let widgetExtraQuery = '';
                let skipWidgetDateFilter = false;
                if (bookedWidgetFilter) {
                    switch (bookedWidgetFilter) {
                        // Task L: 'upcoming_week' (the "N this week" chip inside the Upcoming
                        // card) falls through to the same date-override + extra-query shape as
                        // 'upcoming' — just a narrower window (resolveBookedWidgetDateWindow's
                        // own 'upcoming_week' branch).
                        case 'upcoming':
                        case 'upcoming_week':
                            widgetDateOverride = buildBookedWidgetDateQuery({
                                scope: bookedWidgetFilter,
                                startDate,
                                endDate,
                            });
                            widgetExtraQuery = '&lead_treatments__appointments__status=BOOKED';
                            break;
                        case 'awaiting':
                            // Skip date filter entirely - show all uncontacted BOOKED leads
                            skipWidgetDateFilter = true;
                            widgetExtraQuery = '&lead_treatments__lead_contacted_counts__isnull=true&lead_treatments__appointments__status=BOOKED';
                            break;
                        case 'past':
                            widgetDateOverride = buildBookedWidgetDateQuery({
                                scope: bookedWidgetFilter,
                                startDate,
                                endDate,
                            });
                            widgetExtraQuery = '&lead_treatments__appointments__status=BOOKED';
                            break;
                        default:
                            break;
                    }
                }

                const statusValue = effectiveFilterStatus === 'FOLLOW_UP' ? 'NEW' : effectiveFilterStatus;
                // Bucketed tasks-mode ALWAYS sends the NEW-stage predicate (`lead_treatments__status=NEW`,
                // the same one contacts mode sends) alongside `task_due_bucket`. The server-side
                // bucket filter is an ADDITIONAL narrowing, never the only gate: a backend that
                // does not know a bucket value (e.g. production before `due_now` shipped) has
                // django-filter silently IGNORE task_due_bucket, and without this predicate the
                // table returned every lead of every status (616 on practice 11, incl. BOOKED
                // leads showing under "Select Treatment"). Where the BE does honour the bucket it
                // compiles the same Exists(LeadTreatment status=NEW), so the extra predicate is a
                // harmless redundant AND and the card count == table == rows contract still holds.
                const queryParams = (isBucketOwnedScope
                    ? `lead_treatments__status=NEW&limit=${limit}&offset=${offset}`
                    : ((!effectiveFilterStatus || effectiveFilterStatus === 'All' || (effectiveFilterStatus === 'NEW' && contactedCount === '0') || isAnalyticsEnabled) ? `limit=${limit}&offset=${offset}` : `lead_treatments__status=${statusValue}&limit=${limit}&offset=${offset}`));
                const customStartDate = `${startDate} 00:00:00`;
                const customEndDate = `${endDate} 23:59:00`;
                // Skip user date filter if widget has date override or skipWidgetDateFilter is set
                const date = skipWidgetDateFilter ? '' : (widgetDateOverride || ((startDate && endDate) ?
                    ((effectiveFilterStatus === 'CONSULTATION_BOOKED' || effectiveFilterStatus === 'FAILED_TO_ATTEND_CONSULTATION') && !isAnalyticsEnabled
                        ? `&lead_treatments__appointments__datetime__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` :
                        (isAnalyticsEnabled && effectiveFilterStatus === 'All') ? `&state_transition_params={"timestamp_gte":"${customStartDate}","timestamp_lte":"${customEndDate}"}` :
                            (isV2FlatPath || isAnalyticsEnabled ? `&state_transition_params={"state":"${effectiveFilterStatus}","timestamp_gte":"${customStartDate}","timestamp_lte":"${customEndDate}"}`
                                : `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}`)) : ''));

                // Sanitize treatment: accept comma-string ("1,2") or array ([1,2]/{id}
                // objects); strip any non-numeric tokens so a stale name (e.g. "Veneers")
                // never reaches the backend as `lead_treatments__treatment__in=Veneers`.
                const _normTreatment = (() => {
                    if (!treatment) return null;
                    const tokens = Array.isArray(treatment)
                        ? treatment.map((v) => (v != null && typeof v === 'object' ? v.id : v))
                        : String(treatment).split(',');
                    const ids = tokens
                        .map((t) => (t != null ? String(t).trim() : ''))
                        .filter((t) => t !== '' && !Number.isNaN(Number(t)));
                    return ids.length > 0 ? ids.join(',') : null;
                })();
                // Task #29 guard: the V2 flat endpoint is 1-row-per-treatment by design, so
                // "no-treatment" rows structurally cannot exist there. If noTreatment somehow
                // arrives here (e.g. via a crafted ?noTreatment=true URL param), skip the param
                // entirely — the replaceAll below would otherwise convert
                // `lead_treatments__isnull=true` to `isnull=true` which LeadTreatmentFilter does
                // not support (silently ignored by Django). All real dispatch callers pass
                // noTreatment=false on TREATMENT_STARTED, so this guard has no runtime effect.
                const treatmentQuery = (!isV2FlatPath && noTreatment) ? '&lead_treatments__isnull=true' : _normTreatment ? `&lead_treatments__treatment__in=${_normTreatment}` : '';

                // Task #15 latent-bug guard: contactedCount=undefined/null must not
                // produce contacted_count_for_status=undefined,gte,... in the query.
                // Any caller that omits contactedCount (e.g. AttendedChecklists which
                // uses checklistState exclusively) is safe even without the caller-side fix.
                const newLead = ((type === 'NewLeadTable' || type === 'ConsultantAttend' || type === 'AttendedChecklists' || type === 'ConsultantClinCheckAttend' || type === 'DashNewLeadTable' || (type === 'Dashboard' && (filterStatus === 'NEW' || filterStatus === 'FOLLOW_UP'))) && contactedCount != null && contactedCount !== '100000' && contactedCount !== 'All') ? (contactedCount <= 2 ? filterStatus === 'FOLLOW_UP' ? `&lead_treatments__lead_contacted_counts__count__gte=${contactedCount}` : `&lead_treatments__lead_contacted_counts__count=${contactedCount}` : `&lead_treatments__lead_contacted_counts__count__gte=${contactedCount}`) : null;
                let newLeadParam = newLead;
                if (newLead && (filterStatus === 'CONSULTATION_ATTENDED' || type === 'AttendedChecklists')) {
                    const effectiveStatus = effectiveFilterStatus === 'FOLLOW_UP' ? 'NEW' : effectiveFilterStatus;
                    if (contactedCount <= 2) {
                        newLeadParam = filterStatus === 'FOLLOW_UP'
                            ? `&contacted_count_for_status=${contactedCount},gte,${effectiveStatus}`
                            : `&contacted_count_for_status=${contactedCount},exact,${effectiveStatus}`;
                    } else {
                        newLeadParam = `&contacted_count_for_status=${contactedCount},gte,${effectiveStatus}`;
                    }
                }
                const BlankCountnewLead = (nhsProviderStatus === 'EXISTING' || filterStatus === 'TREATMENT_STARTED' || filterStatus === 'CLOSED' || filterStatus === 'FAILED_TO_ATTEND_CONSULTATION' || contactedCount === '100000') ? '' : filterStatus === 'NEW' ? '&has_new_or_null_treatment=true' : '&lead_treatments__lead_contacted_counts__isnull=true';
                const filterTags = tags !== undefined && tags.length > 0
                    ? (tags === '__NO_TAGS__' ? '&tagged_items__isnull=true' : `&tagged_items__tag__name__in=${tags}`)
                    : '';
                const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';

                const bookedWithFilter = (bookedWith && bookedWith.length) ? (isV2FlatPath ? `&appointments__assignees__user__id__in=${bookedWith}&appointments__type__in=TREATMENT` : `&lead_treatments__appointments__assignees__user__id__in=${bookedWith}`) : '';

                const consultationBookedWithFilter = (consultationBookedWith && consultationBookedWith.length) ? (isV2FlatPath ? `&appointments__assignees__user__id__in=${consultationBookedWith}&appointments__type__in=CONSULTATION` : `&lead_treatments__appointments__assignees__user__id__in=${consultationBookedWith}`) : '';

                const clinCheckStatusQuery = contactedCount === '100000' ? '&lead_treatments__leadtreatment_clincheck__isnull=false' : '';
                // Task #1 fix: DashNewLeadTable "New" sub-tab (contactedCount='All', filterStatus='NEW')
                // must apply has_new_or_null_treatment=true (same as the badge count query) so that
                // already-contacted follow-ups are excluded from the list. NHS tabs (nhsProviderStatus
                // set) are exempt to preserve their existing include_no_treatment_leads behaviour.
                const useBlankCount = ((contactedCount === '0' || contactedCount === 0) && filterStatus !== 'CONSULTATION_ATTENDED' && type !== 'AttendedChecklists') ||
                    (type === 'DashNewLeadTable' && contactedCount === 'All' && filterStatus === 'NEW' && !nhsProviderStatus);
                // In bucketed tasks-mode the BE owns the NEW base, so we drop the
                // has_new_or_null_treatment / contacted-count base param entirely — only
                // task_due_bucket + pagination/ordering go up. Contacts-mode resolution
                // (useBlankCount ? BlankCountnewLead : newLeadParam) is unchanged.
                //
                // Search-in-tasks-mode exception: when a search query is active the bucket
                // filter is lifted so ALL matching NEW leads are returned regardless of their
                // task state.
                //
                // CORRECTED 2026-09-06 — the previous comment here claimed the /leads/search/
                // endpoint "does its own base scoping". It does NOT: LeadViewSet.search
                // (apps/leads/views.py) applies only self.filter_queryset(), so every
                // predicate must arrive as a query param. That false assumption is why the
                // missing NEW filter went unnoticed for three months.
                //
                // In tasks-mode this param still resolves to '' (tasks-mode deliberately
                // sends contactedCount=undefined, so newLeadParam is null and useBlankCount
                // is false). The NEW-stage scope on the search path comes from `queryParams`
                // above (lead_treatments__status=NEW), which LeadFilter compiles to the same
                // Exists(LeadTreatment status=NEW) predicate as apply_new_lead_base_predicates.
                const newLeadBaseParam = isBucketOwnedScope ? '' : (useBlankCount ? BlankCountnewLead : (newLeadParam || ''));
                const search = (searchQuery && searchQuery.length > 0) ? `search/?query=${searchQuery}&${queryParams}${orderingLead}${treatmentQuery}${date}${newLeadBaseParam}${filterTags}${leadReferral}${bookedWithFilter}` : `?${queryParams}${orderingLead}${treatmentQuery}${date}${newLeadBaseParam}${filterTags}${leadReferral}${bookedWithFilter}`;
                const treatmentCategory = (category !== '0' && category) ? isV2FlatPath ? `&treatment__treatment_item__treatment_category__in=${category}` : `&lead_treatments__treatment__treatment_item__treatment_category__in=${category}` : '';
                const consultationBookedMethod = (type === 'ConsultantBooked' && contactedCount !== 'All' && !(typeof contactedCount === 'string' && contactedCount.startsWith('custom:'))) ? `&lead_treatments__appointments__method__in=${contactedCount}` : '';
                const multiStatusQuery = multiStatus ? `&lead_treatments__status__in=${multiStatus}` : '';
                const depositPendingStatusQuery = depositPendingStatus ? `&lead_treatments__is_treatment_fee_pending=${getDepositPendingStatus(depositPendingStatus)}` : '';
                // const appointmentTypeQuery = apptType ? `lead_treatments__appointments__type__in=${apptType}` : '';
                const isDataArchived = isArchived ? `&is_archived=true` : '&is_archived=false';
                const currentTabQuery = currentTab === 'Completed' ? `&lead_treatments__lead_treatment_attributes__key__in=Approval Status&lead_treatment_attributes__contains=${JSON.stringify([{ value: 'Arrived' }])}` : currentTab === 'invisalignFit' ? '&has_null_lead_treatment_attributes_or_not_arrived=true' : currentTab === 'invisalignAdditional' ? '&lead_treatment_attributes__cycle__in=2,3,4,5,6,7' : '';
                const fetchAcrossPractices = acrossPractice === true;
                const practiceFilters = practices?.length ? `&practice__in=${practices}` : ``;

                // All Practices opt-in (phase-2 consumer): append
                // `&practice__in=...` from the global activePracticeIds
                // state so the user's multi-practice selection
                // aggregates across every page the selector reaches,
                // not just analytics. `practiceInSuffix` returns '' for
                // single-practice users — the /practices/{id}/ URL path
                // already scopes them correctly, no need to round-trip
                // the same id through a query param.
                const allPracticesSuffix = practiceInSuffix(
                    requestActivePracticeIds,
                );

                const assigneeIdsFilter = isV2FlatPath ? (assigneeIds ? `&lead__assignees__user__id__in=${assigneeIds}` : '') : (assigneeIds ? `&assignees__user__id__in=${assigneeIds}` : '')

                let apiQuery = isV2FlatPath ? `/api/v1/practices/${id}/lead-treatments-flat/${search}${clinCheckStatusQuery}${treatmentCategory}${consultationBookedMethod}${multiStatusQuery}${depositPendingStatusQuery}${isDataArchived}&${leadFlatFields}&${consultationBookedWithFilter}${getQuery({ isNoTaskTable })}${currentTabQuery}${healthCareProvider}${healthCareProviderQuery}${assigneeIdsFilter}${widgetExtraQuery}${allPracticesSuffix}` :
                    `/api/v1/practices/${id}/leads/${search}${clinCheckStatusQuery}${treatmentCategory}${consultationBookedMethod}${multiStatusQuery}${depositPendingStatusQuery}${isDataArchived}&${resultOnly ? COUNT_ONLY_FIELDS : searchSafeTableFields(searchQuery)}&${consultationBookedWithFilter}${getQuery({ isNoTaskTable })}${currentTabQuery}${healthCareProvider}${healthCareProviderQuery}${assigneeIdsFilter}${widgetExtraQuery}${allPracticesSuffix}`;
                if (isV2FlatPath) {
                    apiQuery = apiQuery.replaceAll("lead_treatments__", "");
                    apiQuery = apiQuery.replaceAll("tagged_items__tag__name__in", "lead__lead_tags__tag__tag__name__in");
                    apiQuery = apiQuery.replaceAll("lead_source__referral__in", "lead__lead_source__referral__in");
                }

                if ((apiQuery?.includes("lead_treatments__lead_contacted_counts__count") || apiQuery?.includes("lead_treatments__lead_contacted_counts__count__gte")) &&
                    !(filterStatus === 'All' || (filterStatus === 'NEW' && contactedCount === '0') || isAnalyticsEnabled || filterStatus === 'CONSULTATION_ATTENDED')) {
                    apiQuery = `${apiQuery}&lead_treatments__lead_contacted_counts__status__in=${filterStatus === 'FOLLOW_UP' ? 'NEW' : filterStatus}`
                }

                if (fetchAcrossPractices) {
                    apiQuery = `/api/v1/leads-flat/${search}${clinCheckStatusQuery}${treatmentCategory}${consultationBookedMethod}${multiStatusQuery}${depositPendingStatusQuery}${isDataArchived}&${searchSafeTableFields(searchQuery)}&${consultationBookedWithFilter}${practiceFilters}${getQuery({ isNoTaskTable })}${currentTabQuery}${healthCareProvider}${healthCareProviderQuery}${assigneeIdsFilter}`
                }
                if (fetchAcrossPractices && leadIdsToSearch) {
                    apiQuery = `/api/v1/leads-flat/?${practiceFilters}&id__in=${leadIdsToSearch?.join(",")}`
                }
                if (directCountFilter) {
                    apiQuery = `${apiQuery}${directCountFilter}`;
                }
                // checklist_state filter — for AttendedChecklists (standalone route),
                // ConsultantAttend, TreatmentStarted, and (task #14) the V2 board list.
                // FE strip value IS the BE value ('in_progress' | 'complete' | 'none');
                // no normalization needed (canonicalized 2026-05-28).
                if (checklistState) {
                    apiQuery = `${apiQuery}&checklist_state=${checklistState}`;
                    // Task #14: V2 board chips now FILTER the list with exhaustive semantics.
                    // checklist_state_exhaustive=true makes In Progress = all non-complete leads
                    // (including unchecklisted + no-treatment). Old BE ignores the param and
                    // filters non-exhaustively (original bug). DEPLOY ORDER: BE must ship first.
                    if (type === 'DashNewLeadTable') {
                        apiQuery = `${apiQuery}&checklist_state_exhaustive=true`;
                    }
                }
                // Tab-contextual attended widget click-through filters.
                // tasksDueNow and noNextAction are resolved entirely BE-side (same scope
                // as the count query that produced the widget number) — the FE never
                // derives or re-computes the matching set. See attended-tab-widgets wave doc.
                if (tasksDueNow) {
                    apiQuery = `${apiQuery}&tasks_due_now=true`;
                    // Attended "Due Now" chip refinement — Overdue / Due Today — ONLY sent
                    // alongside tasks_due_now=true, never on the unrelated NEW-lead bucketed
                    // path above (that one uses task_due_bucket / isBucketOwnedScope). An
                    // unknown or empty value is a no-op server-side, but we still gate it here
                    // to keep the query string clean and avoid sending stale/garbage values.
                    if (tasksDueBucket === 'overdue' || tasksDueBucket === 'due_today') {
                        apiQuery = `${apiQuery}&tasks_due_bucket=${encodeURIComponent(tasksDueBucket)}`;
                    }
                }
                if (noNextAction) {
                    apiQuery = `${apiQuery}&no_next_action=true`;
                }
                // New Lead "Tasks / Due" mode bucket filter — sent only when the user
                // selects a bucket on the task-due widget strip. BE applies the same
                // priority logic (Overdue wins) used by the count endpoint.
                // Suppressed when a search query is active: search shows ALL matching
                // NEW leads regardless of task state (bucket cards become informational
                // count-only; clearing the search restores normal bucket behaviour).
                if (isBucketOwnedScope) {
                    apiQuery = `${apiQuery}&task_due_bucket=${encodeURIComponent(effectiveTaskDueBucket)}`;
                }
                // New Lead tasks-mode annotations — overdue_task_count / next_task_due_at /
                // next_task_title (the row's earliest-due open top-level task). Gated on
                // isBucketedTasksMode (not isBucketOwnedScope) so a tasks-mode search still
                // gets the annotations even though it lifts the bucket base filter.
                if (isBucketedTasksMode) {
                    apiQuery = `${apiQuery}&include_overdue_task_count=true`;
                }
                // Custom treatment-scoped sub-tab — additive layer on top of the parent's resolved query.
                // See docs/superpowers/specs/2026-05-01-custom-treatment-sub-tabs-design.md
                apiQuery += buildCustomTabQuery(id, customTabId, { isV2Flat: isV2FlatPath });
                // V2 board main call-list: surface leads that have no treatment row yet so
                // the count matches V1. Exclude only the V1 bucketed sub-tabs ('0'/'1'/'2'/'3')
                // and the ClinCheck sentinel ('100000') — those scope to specific contacted-count
                // buckets where no-treatment leads never appear. All other DashNewLeadTable calls
                // (contactedCount='All', null, undefined, checklistState sub-tabs) get the param.
                // Note: the list call always passes contactedCount='All' (never null/undefined), so
                // the original == null check was too strict and silently suppressed the param.
                // Backend ignores the param until deployed (graceful degradation by design).
                const _v1Bucket = ['0', '1', '2', '3', '100000'].includes(String(contactedCount));
                // Task #1 fix: skip include_no_treatment_leads for the New sub-tab
                // (contactedCount='All', filterStatus='NEW', no NHS override) — that path
                // now uses has_new_or_null_treatment=true (via useBlankCount above) which
                // already covers no-treatment leads while restricting to uncontacted only.
                const _isDashNewTab = type === 'DashNewLeadTable' && contactedCount === 'All' && filterStatus === 'NEW' && !nhsProviderStatus;
                if (type === 'DashNewLeadTable' && (effectiveFilterStatus === 'NEW' || filterStatus === 'FOLLOW_UP') && !_v1Bucket && !_isDashNewTab) {
                    apiQuery = `${apiQuery}&include_no_treatment_leads=true`;
                }
                if (forceRefetch) apiQuery = `${apiQuery}&_t=${Date.now()}`;
                apiQuery = normalizeLeadTableQuery(apiQuery);
                const response = priority ? await callApi(apiQuery, 'GET', null, false, { priority }) : await callApi(apiQuery);
                if (!resultOnly && !isCurrentRequest()) return;
                if (!resultOnly) dispatch(leadActions.setTreatmentLoading(false));

                if (response?.status === 200) {
                    if (type === 'ConsultantAttend' && !resultOnly) {
                        dispatch(leadActions.clearError());
                    }
                    let result = response.data;
                    if (isLeadTreatmentFlatTablePath(filterStatus, type, forceV1API))
                        result = { ...result, results: result?.results?.map((v2Data, index) => parseV2ToV1(v2Data ?? {}, `${index}${category != null ? `-${category}` : ''}`)).filter((r) => r != null && r.id != null) }

                    // resultOnly intentionally resolves with the payload for local count consumers.
                    // eslint-disable-next-line consistent-return
                    if (resultOnly) return result;

                    if (type === 'DashNewLeadTable') {
                        // Drop stale responses: if another dispatch has incremented
                        // _dashNewLeadToken since this call was dispatched, discard.
                        if (myToken !== _dashNewLeadToken) return;
                        dispatch(leadActions.updateDashNewLead(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'NewLeadTable') {
                        dispatch(leadActions.leadStatusNew(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'ConsultantAttend') {
                        dispatch(leadActions.leadStatusConsultantAttended(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'ConsultantClinCheckAttend') {
                        dispatch(leadActions.leadStatusConsultantClinCheckAttended(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'AttendedChecklists') {
                        dispatch(leadActions.leadStatusAttendedChecklists(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'ConsultantBooked') {
                        dispatch(leadActions.leadStatusConsultantBooked(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'treatmentStarted') {
                        dispatch(leadActions.leadStatusTreatmentStarted(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'FailedToAttend') {
                        dispatch(leadActions.leadStatusFailedToAttend(result));
                        dispatch(startLoadingData(false));
                        return;
                    }
                    if (type === 'Closed') {
                        dispatch(leadActions.leadStatusClosed(result));
                        dispatch(startLoadingData(false));
                        return;
                    }

                    dispatch(leadActions.newLead(result));

                }
                else {
                    if (resultOnly) {
                        throw new Error(response?.detail || response?.error || 'Unable to load lead count');
                    }
                    if (!isCurrentRequest()) return;
                    dispatch(leadActions.setLoadingForExport(false));
                    handleLoading(type, false, isCurrentRequest);
                    dispatch(leadActions.hasError(response.data || response.detail || 'Something went wrong'));
                    return
                }

            } catch (error) {
                if (resultOnly) throw error;
                if (!isCurrentRequest()) return;
                dispatch(leadActions.setTreatmentLoading(false));
                dispatch(leadActions.setLoadingForExport(false));
                dispatch(leadActions.hasError(error.message));
            }
            finally {
                if (!resultOnly) handleLoading(type, false, isCurrentRequest);
            }
        } else {
            try {
                handleLoading(type, true, isCurrentRequest);
                const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
                const customStartDate = `${startDate} 00:00:00`;
                const customEndDate = `${endDate} 23:59:00`;
                const isV2FlatPath = isLeadTreatmentFlatTablePath(filterStatus, type, forceV1API);
                const status = filterStatus === "NEW" ? `&lead_treatments__status=${filterStatus}` : isV2FlatPath ? `&lead_treatments__status=${filterStatus}` : ``;
                const date = (startDate && endDate) ?
                    ((filterStatus === 'CONSULTATION_BOOKED' || filterStatus === 'FAILED_TO_ATTEND_CONSULTATION') && !isAnalyticsEnabled
                        ? `&lead_treatments__appointments__datetime__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` :
                        (isAnalyticsEnabled && (filterStatus === 'All' || filterStatus === 'NEW')) ? `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` :
                            (isV2FlatPath || isAnalyticsEnabled ? `&state_transition_params={"state":"${filterStatus}","timestamp_gte":"${customStartDate}","timestamp_lte":"${customEndDate}"}`
                                : `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}`)) : '';

                const treatmentCategory = (category !== '0' && category) ? isV2FlatPath ? `treatment__treatment_item__treatment_category__in=${category}` : `lead_treatments__treatment__treatment_item__treatment_category__in=${category}` : '';
                const isDataArchived = isArchived ? `&is_archived=true&is_archived_included=true` : '&is_archived=false';

                let result;

                // result = await isCacheValid(cacheNewLead, cacheKey, cacheTimer, currentTime, isCacheApi);

                const dashSuffix = practiceInSuffix(requestActivePracticeIds);
                let apiQuery = isV2FlatPath ? `/api/v1/practices/${id}/lead-treatments-flat/?limit=${limit}&offset=${offset}${status}${orderingLead}&${treatmentCategory}${date}${isDataArchived}&${leadFlatFields}${dashSuffix}` :
                    `/api/v1/practices/${id}/leads/?limit=${limit}&offset=${offset}${status}${orderingLead}&${treatmentCategory}${date}${isDataArchived}&${leadTableFields}${dashSuffix}`;

                if (isV2FlatPath) {
                    apiQuery = apiQuery.replaceAll("lead_treatments__", "");
                    apiQuery = apiQuery.replaceAll("tagged_items__tag__name__in", "lead__lead_tags__tag__tag__name__in");
                    apiQuery = apiQuery.replaceAll("lead_source__referral__in", "lead__lead_source__referral__in");
                }
                const response = await callApi(apiQuery);
                if (!isCurrentRequest()) return;

                if (response?.status === 200) {
                    result = response.data;
                    if (isLeadTreatmentFlatTablePath(filterStatus, type, forceV1API))
                        result = { ...result, results: result?.results?.map((v2Data, index) => parseV2ToV1(v2Data ?? {}, `${index}${category != null ? `-${category}` : ''}`)).filter((r) => r != null && r.id != null) }
                }
                else {
                    dispatch(leadActions.setLoadingForExport(false));
                    handleLoading(type, false, isCurrentRequest);
                    dispatch(leadActions.hasError(response.detail || 'Something went wrong'));
                    return
                }

                dispatch(leadActions.setTreatmentLoading(false));

                if (type === 'DashNewLeadTable') {
                    dispatch(leadActions.updateDashNewLead(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'NewLeadTable') {
                    dispatch(leadActions.leadStatusNew(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'ConsultantAttend') {
                    dispatch(leadActions.leadStatusConsultantAttended(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'ConsultantClinCheckAttend') {
                    dispatch(leadActions.leadStatusConsultantClinCheckAttended(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'AttendedChecklists') {
                    dispatch(leadActions.leadStatusAttendedChecklists(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'ConsultantBooked') {
                    dispatch(leadActions.leadStatusConsultantBooked(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'treatmentStarted') {
                    dispatch(leadActions.leadStatusTreatmentStarted(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'FailedToAttend') {
                    dispatch(leadActions.leadStatusFailedToAttend(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'Closed') {
                    dispatch(leadActions.leadStatusClosed(result));
                    dispatch(startLoadingData(false));
                    return;
                }

                dispatch(leadActions.newLead(result));

            } catch (error) {
                if (!isCurrentRequest()) return;
                dispatch(leadActions.setTreatmentLoading(false));
                dispatch(leadActions.setLoadingForExport(false));
                dispatch(leadActions.hasError(error.message));
            }
            finally {
                handleLoading(type, false, isCurrentRequest);
            }
        }
    };

export const getBlankTreatmentLeads = ({ id, limit, offset, order, orderId, searchQuery, treatment, startDate, endDate, tags, referral, type, nhsProviderStatus, assigneeIds, noTreatment }) =>
    async () => {
        try {
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            const queryParams = `limit=${limit}&offset=${offset}&lead_treatments__isnull=${true}`;
            const date = (startDate && endDate) ? `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : '';
            const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : treatment ? `&lead_treatments__treatment__in=${treatment}` : '';
            const filterTags = tags !== undefined && tags.length > 0
                    ? (tags === '__NO_TAGS__' ? '&tagged_items__isnull=true' : `&tagged_items__tag__name__in=${tags}`)
                    : '';
            const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';
            const search = (searchQuery && searchQuery.length > 0) ? `search/?query=${searchQuery}&${queryParams}${orderingLead}${treatmentQuery}${date}${filterTags}${leadReferral}` : `?${queryParams}${orderingLead}${treatmentQuery}${date}${filterTags}${leadReferral}`;

            // Hardcoded: NHS leads handled on dedicated NHS tab; exclude from main new-lead table.
            let healthCareProviderQuery = '';
            let healthCareProvider = "&healthcare_provider__in=PRIVATE,OTHER";
            if (nhsProviderStatus) {
                healthCareProvider = "&healthcare_provider=NHS";
                healthCareProviderQuery = nhsProviderStatus === "WAITING" ? '&lead_note__note__in=NHS_WAITING_LIST' : nhsProviderStatus === 'NONWAITING' ? '&lead_note__note__not_in=NHS_WAITING_LIST' : '&is_registered_patient=true';
            }
            const assigneeIdsFilter = assigneeIds ? `&assignees__user__id__in=${assigneeIds}` : '';

            const response = await callApi(`/api/v1/practices/${id}/leads/${search}${healthCareProvider}${healthCareProviderQuery}&is_archived=false&${searchSafeTableFields(searchQuery)}${assigneeIdsFilter}${practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds)}`);
            if (response?.status === 200) {
                if (type === 'DashNewLeadTable') {
                    dispatch(leadActions.updateDashNewLead(response.data));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'NewLeadTable') {
                    dispatch(leadActions.leadStatusNew(response.data));
                    dispatch(startLoadingData(false));
                }
            } else {
                dispatch(leadActions.hasError(response.detail || 'Something went wrong'));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error.message));
        }
    };

export const getNHSLeads = ({ id, limit, offset, order, orderId, searchQuery, treatment, startDate, endDate, tags, referral, type, healthCareProvider, assigneeIds, noTreatment }) =>
    async () => {
        try {
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            const queryParams = `lead_treatments__status=NEW&limit=${limit}&offset=${offset}`;
            const date = (startDate && endDate) ? `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : '';
            const nhsLead = healthCareProvider ? `&healthcare_provider=${healthCareProvider}` : '';
            const filterTags = tags !== undefined && tags.length > 0
                    ? (tags === '__NO_TAGS__' ? '&tagged_items__isnull=true' : `&tagged_items__tag__name__in=${tags}`)
                    : '';
            const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';
            const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : treatment ? `&lead_treatments__treatment__in=${treatment}` : '';
            const search = (searchQuery && searchQuery.length > 0) ? `search/?query=${searchQuery}&${queryParams}${orderingLead}${nhsLead}${date}${filterTags}${leadReferral}${treatmentQuery}` : `?${queryParams}${orderingLead}${nhsLead}${date}${filterTags}${leadReferral}${treatmentQuery}`;

            const assigneeIdsFilter = assigneeIds ? `&assignees__user__id__in=${assigneeIds}` : '';

            const response = await callApi(`/api/v1/practices/${id}/leads/${search}&is_archived=false&${searchSafeTableFields(searchQuery)}&lead_note__note__not_in=NHS_WAITING_LIST${assigneeIdsFilter}${practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds)}`);

            if (response?.status === 200) {
                const result = response.data;
                if (type === 'DashNewLeadTable') {
                    dispatch(leadActions.updateDashNewLead(result));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'NewLeadTable') {
                    dispatch(leadActions.leadStatusNew(result));
                    dispatch(startLoadingData(false));
                }

            } else {
                dispatch(leadActions.hasError(response.detail || 'Something went wrong'));
            }

        } catch (error) {
            dispatch(leadActions.hasError(error.message));
        }
    };

export const getNoTreatmentCounter = ({ id, limit, offset, order, orderId, searchQuery, treatment, startDate, endDate, tags, referral, nhsProviderStatus, assigneeIds, noTreatment }) =>
    async () => {
        try {
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            const queryParams = `limit=${limit}&offset=${offset}&lead_treatments__isnull=${true}`;
            const date = (startDate && endDate) ? `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : '';
            const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : treatment ? `&lead_treatments__treatment__in=${treatment}` : '';
            const filterTags = tags !== undefined && tags.length > 0
                    ? (tags === '__NO_TAGS__' ? '&tagged_items__isnull=true' : `&tagged_items__tag__name__in=${tags}`)
                    : '';
            const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';
            const search = (searchQuery && searchQuery.length > 0) ? `search/?query=${searchQuery}&${queryParams}${orderingLead}${treatmentQuery}${date}${filterTags}${leadReferral}` : `?${queryParams}${orderingLead}${treatmentQuery}${date}${filterTags}${leadReferral}`;

            // Hardcoded: NHS leads handled on dedicated NHS tab; exclude from main new-lead table.
            let healthCareProviderQuery = '';
            let healthCareProvider = "&healthcare_provider__in=PRIVATE,OTHER";
            if (nhsProviderStatus) {
                healthCareProvider = "&healthcare_provider=NHS";
                healthCareProviderQuery = nhsProviderStatus === "WAITING" ? '&lead_note__note__in=NHS_WAITING_LIST' : nhsProviderStatus === 'NONWAITING' ? '&lead_note__note__not_in=NHS_WAITING_LIST' : '&is_registered_patient=true';
            }
            const assigneeIdsFilter = assigneeIds ? `&assignees__user__id__in=${assigneeIds}` : '';

            const response = await callApi(`/api/v1/practices/${id}/leads/${search}&is_archived=false${healthCareProvider}${healthCareProviderQuery}&fields={id}${assigneeIdsFilter}${practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds)}`);
            if (response?.status === 200) {
                const result = response.data;
                const { counter } = store.getState().lead;
                dispatch(leadActions.updateCounter([...counter, { filterStatus: 'No Treatment', count: result?.count }]));

            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }

        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
        }
        finally {
            dispatch(startLoadingData(false));
        }
    };

export const getNHSCounter = ({ id, limit, offset, order, orderId, searchQuery, treatment, startDate, endDate, tags, referral, healthCareProvider, assigneeIds, noTreatment }) =>
    async () => {
        try {
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            const queryParams = `lead_treatments__status=NEW&limit=${limit}&offset=${offset}`;
            const date = (startDate && endDate) ? `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : '';
            const nhsLead = healthCareProvider ? `&healthcare_provider=${healthCareProvider}` : '';
            const filterTags = tags !== undefined && tags.length > 0
                    ? (tags === '__NO_TAGS__' ? '&tagged_items__isnull=true' : `&tagged_items__tag__name__in=${tags}`)
                    : '';
            const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';
            const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : treatment ? `&lead_treatments__treatment__in=${treatment}` : '';
            const search = (searchQuery && searchQuery.length > 0) ? `search/?query=${searchQuery}&${queryParams}${orderingLead}${nhsLead}${date}${filterTags}${leadReferral}${treatmentQuery}` : `?${queryParams}${orderingLead}${nhsLead}${date}${filterTags}${leadReferral}${treatmentQuery}`;
            const assigneeIdsFilter = assigneeIds ? `&assignees__user__id__in=${assigneeIds}` : '';

            const response = await callApi(`/api/v1/practices/${id}/leads/${search}&is_archived=false&fields={id}&lead_note__note__not_in=NHS_WAITING_LIST${assigneeIdsFilter}${practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds)}`);

            if (response?.status === 200) {
                const result = response.data;
                const { counter } = store.getState().lead;
                dispatch(leadActions.updateCounter([...counter, { filterStatus: 'NHS', count: result?.count }]));

            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }

        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
        }
        finally {
            dispatch(startLoadingData(false));
        }
    };

export function getAwaitingLead({ id, type, data, limit, offset, order, orderId, searchQuery, treatment, startDate, endDate, tags, referral, nhsProviderStatus, assigneeIds, noTreatment, customTabId, countOnly }) {
    return async () => {
        try {

            // Hardcoded: NHS leads handled on dedicated NHS tab; exclude from main new-lead table.
            let healthCareProviderQuery = '';
            let healthCareProvider = "&healthcare_provider__in=PRIVATE,OTHER";
            if (nhsProviderStatus) {
                healthCareProvider = "&healthcare_provider=NHS";
                healthCareProviderQuery = nhsProviderStatus === "WAITING" ? '&lead_note__note__in=NHS_WAITING_LIST' : nhsProviderStatus === 'NONWAITING' ? '&lead_note__note__not_in=NHS_WAITING_LIST' : '&is_registered_patient=true';
            }
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            // countOnly (badge-counter callers): the response's `count` field reflects
            // the total matching rows regardless of limit/fields, so we only need 1
            // row's worth of the cheapest possible field set — never the full
            // searchSafeTableFields table payload the AWAITING sub-tab table renders.
            const effectiveLimit = countOnly ? 1 : limit;
            const queryParams = `limit=${effectiveLimit}&offset=${offset}&latest_note__in=AWAITING_RESPONSE`;
            const date = (startDate && endDate) ? `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : '';
            const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : treatment ? `&lead_treatments__treatment__in=${treatment}` : '';
            const filterTags = tags !== undefined && tags.length > 0
                    ? (tags === '__NO_TAGS__' ? '&tagged_items__isnull=true' : `&tagged_items__tag__name__in=${tags}`)
                    : '';
            const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';
            const filterData = '&lead_treatments__status=NEW';
            const search = (searchQuery && searchQuery.length > 0) ? `search/?query=${searchQuery}&${queryParams}${orderingLead}${treatmentQuery}${date}${filterTags}${leadReferral}${filterData}` : `?${queryParams}${orderingLead}${treatmentQuery}${date}${filterTags}${leadReferral}${filterData}`;
            const assigneeIdsFilter = assigneeIds ? `&assignees__user__id__in=${assigneeIds}` : '';

            // Custom-tab additive filter — Awaiting uses V1 leads endpoint (isV2Flat=false).
            const customTabQuery = buildCustomTabQuery(id, customTabId);

            // Same minimal `fields={id}` shape already proven safe on both /leads/ and
            // /leads/search/ by getNoTreatmentCounter/getNHSCounter above — keeps the
            // count-only request working identically whether or not a search is active.
            const fieldsQuery = countOnly ? 'fields={id}' : searchSafeTableFields(searchQuery);

            const response = await callApi(`/api/v1/practices/${id}/leads/${search}&is_archived=false${healthCareProvider}${healthCareProviderQuery}&${fieldsQuery}${assigneeIdsFilter}${customTabQuery}${practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds)}`);

            if (response?.status === 200) {
                const result = response.data;
                const { counter } = store.getState().lead;
                dispatch(leadActions.updateCounter([...counter, { key: 'AWAITING_RESPONSE', filterStatus: 'AWAITING_RESPONSE', count: result?.count }]));

                if (data === 'DashNewLeadTable') {
                    dispatch(leadActions.updateDashNewLead(result));
                    return;
                }
                if (data === 'NewLeadTable' && type === 'AddData') {
                    dispatch(leadActions.leadStatusNew(result));
                }

            }
            else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }

        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
        }
        finally {
            dispatch(startLoadingData(false));
        }
    };
}

export const getCounter = ({ id, filterStatus, order, orderId, searchQuery, treatment, startDate, endDate, contactedCount, tags, referral, type, bookedWith, depositPendingStatus, isNoTaskTable, multiStatus, category, isArchived, nhsProviderStatus, assigneeIds, forceV1API, noTreatment, getCounter, acrossPractice, practices, leadIdsToSearch, checklistState, forceRefetch, customTabId }) =>
    async () => {
        try {

            // Hardcoded: NHS leads handled on dedicated NHS tab; exclude from main new-lead table.
            let healthCareProviderQuery = '';
            let healthCareProvider = isArchived ? '' : "&healthcare_provider__in=PRIVATE,OTHER";
            if (nhsProviderStatus) {
                healthCareProvider = "&healthcare_provider=NHS";
                healthCareProviderQuery = nhsProviderStatus === "WAITING" ? '&lead_note__note__in=NHS_WAITING_LIST' : nhsProviderStatus === 'NONWAITING' ? '&lead_note__note__not_in=NHS_WAITING_LIST' : '&is_registered_patient=true';
            }
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            const counterStatusValue = filterStatus === 'FOLLOW_UP' ? 'NEW' : filterStatus;
            const queryParams = (!filterStatus || filterStatus === 'All' || (filterStatus === 'NEW' && contactedCount === '0')) ? `limit=${1}&offset=${0}` : `lead_treatments__status=${counterStatusValue}&limit=${1}&offset=${0}`;
            const customStartDate = `${startDate} 00:00:00`;
            const customEndDate = `${endDate} 23:59:00`;
            const isV2FlatPath = isLeadTreatmentFlatTablePath(filterStatus, type, forceV1API);
            const date = (startDate && endDate) ? (filterStatus === 'CONSULTATION_BOOKED' || filterStatus === 'FAILED_TO_ATTEND_CONSULTATION' ? `&lead_treatments__appointments__datetime__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : (isV2FlatPath ? `&state_transition_params={"state":"${filterStatus}","timestamp_gte":"${customStartDate}","timestamp_lte":"${customEndDate}"}` : `&created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}`)) : '';
            // Same sanitize guard as getNewLead — strips names/undefined/null tokens.
            const _normTreatmentC = (() => {
                if (!treatment) return null;
                const tokens = Array.isArray(treatment)
                    ? treatment.map((v) => (v != null && typeof v === 'object' ? v.id : v))
                    : String(treatment).split(',');
                const ids = tokens
                    .map((t) => (t != null ? String(t).trim() : ''))
                    .filter((t) => t !== '' && !Number.isNaN(Number(t)));
                return ids.length > 0 ? ids.join(',') : null;
            })();
            // Task #29 guard: same as getNewLead — V2 flat path cannot have no-treatment rows;
            // noTreatment on this path would produce unsupported &isnull=true after replaceAll.
            const treatmentQuery = (!isV2FlatPath && noTreatment) ? '&lead_treatments__isnull=true' : _normTreatmentC ? `&lead_treatments__treatment__in=${_normTreatmentC}` : '';
            const newLead = ((type === 'NewLeadTable' || type === 'ConsultantAttend' || type === 'AttendedChecklists' || type === 'ConsultantClinCheckAttend' || type === 'DashNewLeadTable' || type === 'Dashboard') && contactedCount != null && contactedCount !== '100000' && contactedCount !== 'All') ? (contactedCount <= 2 ? filterStatus === 'FOLLOW_UP' ? `&lead_treatments__lead_contacted_counts__count__gte=${contactedCount}` : `&lead_treatments__lead_contacted_counts__count=${contactedCount}` : `&lead_treatments__lead_contacted_counts__count__gte=${contactedCount}`) : null;
            let newLeadParam = newLead;
            if (newLead && (filterStatus === 'CONSULTATION_ATTENDED' || type === 'AttendedChecklists')) {
                const effectiveStatus = filterStatus === 'FOLLOW_UP' ? 'NEW' : filterStatus;
                if (contactedCount <= 2) {
                    newLeadParam = filterStatus === 'FOLLOW_UP'
                        ? `&contacted_count_for_status=${contactedCount},gte,${effectiveStatus}`
                        : `&contacted_count_for_status=${contactedCount},exact,${effectiveStatus}`;
                } else {
                    newLeadParam = `&contacted_count_for_status=${contactedCount},gte,${effectiveStatus}`;
                }
            }
            const BlankCountnewLead = (filterStatus === 'TREATMENT_STARTED' || filterStatus === 'CLOSED' || filterStatus === 'FAILED_TO_ATTEND_CONSULTATION' || contactedCount === '100000') ? '' : filterStatus === 'NEW' ? '&has_new_or_null_treatment=true' : '&lead_treatments__lead_contacted_counts__isnull=true';
            const filterTags = tags && tags.length > 0
                    ? (tags === '__NO_TAGS__' ? '&tagged_items__isnull=true' : `&tagged_items__tag__name__in=${tags}`)
                    : '';
            const leadReferral = referral?.length > 0 ? `&lead_source__referral__in=${referral}` : '';
            const bookedWithFilter = (bookedWith && bookedWith.length) ? (isV2FlatPath ? `&appointments__assignees__user__id__in=${bookedWith}&appointments__type__in=TREATMENT` : `&lead_treatments__appointments__assignees__user__id__in=${bookedWith}`) : '';

            const clinCheckStatusQuery = contactedCount === '100000' ? '&lead_treatments__leadtreatment_clincheck__isnull=false' : '';
            const depositPendingStatusQuery = depositPendingStatus ? `&lead_treatments__is_treatment_fee_pending=${getDepositPendingStatus(depositPendingStatus)}` : '';
            const multiStatusQuery = multiStatus ? `&lead_treatments__status__in=${multiStatus}` : '';
            const treatmentCategory = (category !== '0' && category) ? isV2FlatPath ? `&treatment__treatment_item__treatment_category__in=${category}` : `&lead_treatments__treatment__treatment_item__treatment_category__in=${category}` : '';
            const useBlankCount = (contactedCount === '0' || contactedCount === 0) && filterStatus !== 'CONSULTATION_ATTENDED' && type !== 'AttendedChecklists';
            const search = (searchQuery && searchQuery !== null && searchQuery.length > 0) ? `search/?query=${searchQuery}&${queryParams}${orderingLead}${treatmentQuery}${date}${useBlankCount ? BlankCountnewLead : (newLeadParam || '')}${filterTags}${leadReferral}${bookedWithFilter}` : `?${queryParams}${orderingLead}${treatmentQuery}${date}${useBlankCount ? BlankCountnewLead : (newLeadParam || '')}${filterTags}${leadReferral}${bookedWithFilter}${clinCheckStatusQuery}`;
            const isDataArchived = isArchived ? `&is_archived_included=true` : '&is_archived=false';
            const fetchAcrossPractices = acrossPractice === true;
            const assigneeIdsFilter = isV2FlatPath ? (assigneeIds ? `&lead__assignees__user__id__in=${assigneeIds}` : '') : (assigneeIds ? `&assignees__user__id__in=${assigneeIds}` : '')
            const practiceFilters = practices?.length ? `&practice__in=${practices}` : ``;
            // All Practices phase-2: mirror the suffix from
            // activePracticeIds onto the counter endpoint too so widget
            // badges match the multi-practice lead-table rows. Empty
            // string for single-practice selection — existing URL scope
            // already covers that case.
            const allPracticesSuffix = practiceInSuffix(
                store.getState().practiceTreatmentList?.activePracticeIds,
            );

            let apiQuery = isV2FlatPath ? `/api/v1/practices/${id}/lead-treatments-flat/${search}${depositPendingStatusQuery}${getQuery({ isNoTaskTable })}&${multiStatusQuery}&fields={}${isDataArchived}${healthCareProvider}${healthCareProviderQuery}${assigneeIdsFilter}${allPracticesSuffix}`
                : `/api/v1/practices/${id}/leads/${search}${depositPendingStatusQuery}${getQuery({ isNoTaskTable })}&${multiStatusQuery}&fields={}${isDataArchived}${healthCareProvider}${healthCareProviderQuery}${assigneeIdsFilter}${allPracticesSuffix}`;
            if (isV2FlatPath) {
                apiQuery = apiQuery.replaceAll("lead_treatments__", "");
                apiQuery = apiQuery.replaceAll("lead_source__referral__in", "lead__lead_source__referral__in");
                apiQuery = apiQuery.replaceAll("tagged_items__tag__name__in", "lead__lead_tags__tag__tag__name__in");
            }

            if ((apiQuery?.includes("lead_treatments__lead_contacted_counts__count") || apiQuery?.includes("lead_treatments__lead_contacted_counts__count__gte")) &&
                !(filterStatus === 'All' || (filterStatus === 'NEW' && contactedCount === '0') || filterStatus === 'CONSULTATION_ATTENDED')) {
                apiQuery = `${apiQuery}&lead_treatments__lead_contacted_counts__status__in=${filterStatus === 'FOLLOW_UP' ? 'NEW' : filterStatus}`
            }

            if (fetchAcrossPractices) {
                apiQuery = `/api/v1/leads-flat/${search}${depositPendingStatusQuery}${getQuery({ isNoTaskTable })}&${multiStatusQuery}&fields={}${isDataArchived}${healthCareProvider}${healthCareProviderQuery}${assigneeIdsFilter}${practiceFilters}`
            }

            if (fetchAcrossPractices && leadIdsToSearch) {
                apiQuery = `/api/v1/leads-flat/?${practiceFilters}&id__in=${leadIdsToSearch?.join(",")}`
            }

            if (checklistState) {
                // FE strip value is the canonical BE value — no normalization (2026-05-28).
                apiQuery = `${apiQuery}&checklist_state=${checklistState}`;
            }
            // Task #13: V2 board In-Progress / Follow-Up-Complete badge calls need
            // exhaustive counting so All == IP + FC. Graceful degradation: old BE
            // ignores the param so badge math is unchanged until BE deploys.
            // Intentionally narrow to DashNewLeadTable only — ConsultantAttend,
            // AttendedChecklists, and TreatmentStarted surfaces are unaffected.
            if (type === 'DashNewLeadTable' && checklistState) {
                apiQuery = `${apiQuery}&checklist_state_exhaustive=true`;
            }

            // Custom treatment-scoped sub-tab — same endpoint family as the list path.
            apiQuery += buildCustomTabQuery(id, customTabId, { isV2Flat: isV2FlatPath });
            // Mirror of getNewLead: ensures the V2 "New Leads" headline badge includes
            // no-treatment leads. Checklist-state calls are excluded — they count leads
            // with checklists only, and no-treatment leads never have a checklist.
            if (type === 'DashNewLeadTable' && (filterStatus === 'NEW' || filterStatus === 'FOLLOW_UP') && contactedCount == null && !checklistState) {
                apiQuery = `${apiQuery}&include_no_treatment_leads=true`;
            }

            if (forceRefetch) apiQuery = `${apiQuery}&_t=${Date.now()}`;
            const response = await callApi(apiQuery);

            if (response?.status === 200) {
                const result = response.data;
                const { counter } = store.getState().lead;
                if (type === 'DashNewLeadTable') {
                    // Namespace checklist keys by type to prevent cross-table counter
                    // pollution: DashNewLeadTable (exhaustive) and ConsultantAttend
                    // (non-exhaustive) both write 'in_progress'/'complete' — without a
                    // namespace, last writer wins and the stale count renders on the
                    // other surface. Contacted-count keys are not namespaced (separate fix).
                    const key = checklistState ? `${type}:${checklistState}` : contactedCount;
                    dispatch(leadActions.updateCounter([...counter, { filterStatus: key, count: result?.count }]));
                    return;
                }
                if (type === 'NewLeadTable') {
                    dispatch(leadActions.updateCounter([...counter, { key: contactedCount, filterStatus: contactedCount, count: result?.count }]));

                    return;
                }
                if (type === 'ConsultantAttend') {
                    const key = checklistState ? `${type}:${checklistState}` : contactedCount;
                    dispatch(leadActions.updateCounter([...counter, { filterStatus: key, count: result?.count }]));
                    return;
                }
                if (type === 'ConsultantClinCheckAttend') {
                    dispatch(leadActions.updateCounter([...counter, { filterStatus: contactedCount, count: result?.count }]));
                    return;
                }
                if (type === 'AttendedChecklists') {
                    const key = checklistState ? `${type}:${checklistState}` : contactedCount;
                    dispatch(leadActions.updateCounter([...counter, { filterStatus: key, count: result?.count }]));
                    return;
                }
                if (type === 'treatmentStarted') {
                    // Treatment Started has no contacted-count strip — only the
                    // checklist sub-strip. Key the count by its checklist state so
                    // NewLead's tabCounter resolves it via the namespaced key.
                    const key = checklistState ? `${type}:${checklistState}` : filterStatus;
                    dispatch(leadActions.updateCounter([...counter, { filterStatus: key, count: result?.count }]));
                    return;
                }
                dispatch(leadActions.updateCounter([...counter, { filterStatus, count: result?.count }]));

            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }


        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
        }
        finally {
            dispatch(startLoadingData(false));
        }
    };

export function dashNewTableCounter(id) {
    return async () => {

        // Hardcoded: NHS leads handled on dedicated NHS tab; exclude from main new-lead table.
        const response = await callApi(`/api/v1/practices/${id}/leads/?has_new_or_null_treatment=true&fields={}&is_archived=false&healthcare_provider__in=PRIVATE,OTHER${practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds)}`);
        try {
            if (response?.status === 200) {
                const { dashNewContact } = store.getState().lead;
                dispatch(updateDashNewLeadsTable({ ...dashNewContact, labelCounter: response.data.count }));
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data) || 'Something went wrong', success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
        }
    };
}

// ── Lead CRUD ────────────────────────────────────────────────

// Describe the creation settings, not unobserved delivery outcomes.
const MANUAL_LEAD_ACK_SUPPRESSED_MESSAGE = 'Lead created. Automatic patient acknowledgements are disabled; practice emails are enabled for this creation.';
const MANUAL_LEAD_FULLY_SILENT_MESSAGE = 'Lead created. Automatic patient acknowledgements and practice emails are disabled for this creation.';

// The 201 body reports the Dentally link outcome without failing the create.
const reportDentallyLink = (data) => {
    const link = data?.dentally_link;
    if (link?.status === 'conflict' || link?.status === 'error' || link?.status === 'not_verified') {
        dispatch(updateError({
            success: 'warning',
            message: link.status === 'conflict'
                ? 'Lead created, but that Dentally patient is already linked to another lead. Link it manually from the lead if needed.'
                : 'Lead created, but the Dentally patient could not be linked. You can link it from the lead.',
        }));
    }
};

export function savePersonalDetails(data, practiceId, handleClose) {
    return async () => {
        try {
            const {
                lead, activeTab, newLeadDetail, sendReminderDuringLeadCreate,
                notifyPracticeDuringLeadCreate,
            } = store.getState().lead;
            // Missing or stale state must not opt an operator into practice email.
            const notifyPractice = notifyPracticeDuringLeadCreate === true;
            const treatmentDetail = activeTab >= 1 && newLeadDetail?.lead_treatment?.treatment
                ? { ...newLeadDetail.lead_treatment, is_practice_notification_required: false }
                : null;
            const payload = mergeAdAttributionIntoLeadPayload(buildManualLeadPayload({
                ...data,
                suppress_initial_message_sequence: true,
                suppress_practice_notification: !notifyPractice,
            }, treatmentDetail));
            const initialResolution = store.getState().createLeadDialog?.identity?.resolution || null;
            const capture = newLeadDetail?.attendanceCapture;
            let outcome;
            if (capture) {
                if (Number(capture.practiceId) !== Number(practiceId)) throw new Error('Practice changed. Reopen the attendance step before creating this patient.');
                const { teethAmount, ...atomicTreatment } = treatmentDetail || {};
                if (teethAmount != null) atomicTreatment.leadtreatment_specification = { teeth_count: teethAmount };
                const result = await captureAdvancedLeadCreate({ practiceId: Number(practiceId), operationKey: capture.operationKey,
                    targetStatus: capture.targetStatus, decision: capture.decision, leadPayload: payload, treatment: atomicTreatment, identityResolution: initialResolution });
                outcome = result.status === 'cancelled' ? { cancelled: true } : { response: result.data };
            } else outcome = await postManualLead(practiceId, payload, { timeout: 30000, strictPhone: true }, initialResolution);
            const { response, cancelled } = outcome;
            if (cancelled) {
                // Operator backed out of the identity dialog: nothing was created.
                dispatch(updateCompleteStatus(false));
                dispatch(startLoadingData(false));
                return;
            }
            if (response?.status === 201 || (capture && response?.status === 200)) {
                reportDentallyLink(response?.data);
                // Clear stashed ad attribution after successful lead create
                // so subsequent leads in the same session don't all carry
                // the same gclid / UTMs.
                clearStoredAdAttribution();
                // A 201 has persisted the lead even if its nested response is malformed.
                dispatch(leadActions.saveConsentLead(response?.data));
                dispatch(leadActions.saveLead(response?.data));
                const createdTreatment = getCreatedManualLeadTreatment(response?.data, treatmentDetail);
                if (!capture && sendReminderDuringLeadCreate) {
                    const { lead_treatment: ignoredTreatment, ...reminderDetails } = newLeadDetail?.lead_reminder || {};
                    const addReminderObj = {
                        ...reminderDetails,
                        lead: response?.data?.id,
                        ...(createdTreatment ? { lead_treatment: createdTreatment.id } : {}),
                    };
                    dispatch(addReminder(addReminderObj, practiceId, '', 'createLead'));
                }
                dispatch(updateError({
                    success: true,
                    message: notifyPractice
                        ? MANUAL_LEAD_ACK_SUPPRESSED_MESSAGE
                        : MANUAL_LEAD_FULLY_SILENT_MESSAGE,
                }));
                const results = [response?.data, ...lead?.results];
                const count = lead?.count + 1;
                dispatch(leadActions.updateLead({ results, count }));
                if (capture) {
                    dispatch(leadActions.updateSendReminderDuringLeadCreate(false));
                    if (createdTreatment) dispatch(leadActions.saveTreatment(createdTreatment));
                    dispatch(invalidateAttendance({ practiceId, leadId: response?.data?.id, treatmentId: createdTreatment?.id }));
                    dispatch(startLoadingData(false));
                    handleClose?.('finish', response?.data?.id);
                } else if (createdTreatment) {
                    await dispatch(continueCreatedTreatment(createdTreatment, treatmentDetail, practiceId, response?.data?.id, handleClose, { needToUpdate: treatmentDetail.teethAmount || treatmentDetail.teethAmount === 0, teeth: treatmentDetail.teethAmount || 0 }));
                } else {
                    // If no treatment to add, call handleClose directly to close the dialog
                    handleClose?.('finish', response?.data?.id);
                }
                // handleNext?.({ success: true, message: 'Personal Details added Successfully' });
            } else {
                dispatch(updateCompleteStatus(false));
                // `status` is load-bearing: errorHandler.jsx treats a missing
                // status as a network error and ships it to Sentry (4xx must be toast-only).
                dispatch(updateError({ message: friendlyCreateLeadError(response), success: false, status: response?.status }));
                dispatch(startLoadingData(false));
                // handleNext?.({ success: false, message: response.data || 'Something went wrong' });
                // dispatch(leadActions.hasError(response.data || 'Something went wrong'));
            }
        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
            dispatch(startLoadingData(false));
        }
    };
}

export function updatePersonalDetail(data, practiceId, handleClose, type) {
    return async () => {
        try {
            // ?phone_check=strict: a CHANGED number the desk phone can't dial is refused (CU-869fnwgxf).
            const response = await callApi(withStrictPhoneCheck(`/api/v1/practices/${practiceId}/leads/${data.id}/`), 'patch', data);
            if (response?.status === 200) {
                const { activeTab, newLeadDetail, attendance, tableRowLead, newLeadDetail: { archiveLead } } = store.getState().lead;
                if (type === 'leadDetail') {
                    dispatch(leadActions.saveLeadDetail(response.data));
                    dispatch(followUpSlice(data.id, practiceId, 'LeadDetail'));
                    dispatch(updateError({ success: true, message: 'Personal details updated successfully' }));
                    handleClose('finish', data.id);
                    dispatch(startLoadingData(false));
                    return;
                }
                if (type === 'personalDetail') {
                    dispatch(leadActions.saveLead(response.data));
                }
                if (attendance === 'payment' && activeTab > 2 && newLeadDetail.payment.payment_type !== 'No') {
                    dispatch(leadTreatmentPayment(practiceId, tableRowLead.lead_id, tableRowLead.treatment_id, newLeadDetail.payment, handleClose));
                    return;
                }
                if (type === 'archived') {
                    dispatch(archivedLeadAPI(archiveLead?.archiveData, practiceId, handleClose, data.id));
                    return;
                }
                if (type === 'leadDataGrid') {
                    dispatch(updateError({ success: true, message: 'Personal details updated successfully' }));
                    return;
                }
                if (type === 'leadTableEdit') {
                    handleClose({ success: true, message: 'Personal details updated successfully', data: response.data });
                    return;
                }
                if (type === 'EditPersonalDetail') {
                    dispatch(leadActions.saveLead(response.data));
                    dispatch(updateError({ success: true, message: 'Personal details updated successfully' }));
                    handleClose();
                    // A phone edit on the open lead page: reload its detail so the
                    // "Number looks wrong" badge and Call follow the new number (CU-869fnwgxf).
                    // Identical GETs share one request (callApi dedupe), so this costs nothing extra.
                    dispatch(followUpSlice(data.id, practiceId));
                    if (store.getState().lead?.leadDetail?.id === data.id && data.lead_contact?.phone !== undefined) {
                        dispatch(followUpSlice(data.id, practiceId, 'LeadDetail'));
                    }
                    dispatch(getAppointment(practiceId, data.id));
                    return;
                }
                dispatch(updateError({ success: true, message: 'Personal details updated successfully' }));
                dispatch(updateCompleteStatus(true));
                if (type === 'leadDetail') {
                    handleClose(true);
                    return;
                }
                handleClose('finish', data.id);
                dispatch(startLoadingData(false));
            } else {
                dispatch(updateCompleteStatus(false));
                const phoneMessage = phoneSaveError(response);
                dispatch(updateError({ message: phoneMessage ? `Phone number: ${phoneMessage}` : JSON.stringify(response.data), success: false }));
                dispatch(startLoadingData(false));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error.message));
            dispatch(startLoadingData(false));
        }
    };
}

export function createNewLead(handleClose) {
    return async () => {
        try {
            const { newLeadDetail } = store.getState().lead;
            const { practiceId } = store.getState().practiceTreatmentList;

            // Validate that personalDetail exists and has required fields before making API call
            const personalDetail = newLeadDetail?.personalDetail;

            if (!personalDetail || (!personalDetail.first_name && !personalDetail.lead_contact?.phone)) {
                dispatch(updateError({ message: 'Cannot create lead: Please fill in the required fields (First name and Phone number)', success: false }));
                dispatch(startLoadingData(false));
                dispatch(updateCompleteStatus(false));
                return;
            }

            dispatch(startLoadingData(true));
            dispatch(savePersonalDetails(personalDetail, practiceId, handleClose));
        } catch (error) {
            dispatch(updateError({ message: error.message, success: false, cause: error }));
            dispatch(startLoadingData(false));
        }
    };
}

export const getCurrentTreatmentData = (data) => {
    const { currentTreatmentDataGlobal } = store.getState().multiTreatment;
    if (!isEmpty(currentTreatmentDataGlobal?.treatmentData)) return currentTreatmentDataGlobal.treatmentData;
    return data;
};

export function updateLeadDetail(data, handleClose, isNoNotes, isRecursiveCall = false, bookingCompletion = null) {
    return async () => {
        try {
            if (bookingCompletion) {
                const { practiceId, leadId, treatmentId, appointment, treatment, isScopeCurrent } = bookingCompletion;
                if (!isScopeCurrent()) return;
                const original = store.getState().lead.followLead?.lead_treatments?.find((row) => String(row.id) === String(treatmentId));
                if (!original) throw new Error('Select the exact treatment before booking.');
                dispatch(startLoadingData(true));
                dispatch(setLoader(true));
                // The Overview owns this completion. Never recover its target or
                // form values from Redux after treatment/appointment requests.
                // eslint-disable-next-line eqeqeq
                if (original.treatment !== treatment.treatment || original.price != treatment.custom_price || original.leadtreatment_specification?.teeth_count != treatment.teethAmount) {
                    await dispatch(UpdateTreatmentV2(treatment, practiceId, handleClose, 'updateLeadDetail', { bookingCompletion }));
                } else {
                    await dispatch(addAppointment(appointment, practiceId, leadId, handleClose, 'followUp', undefined, isScopeCurrent, bookingCompletion));
                }
                return;
            }
            const { newLeadDetail: { lead_appointment: leadAppoiment, archiveLead, lead_treatment: backupLeadTreatment }, followLead, newLeadDetail, activeTab } = store.getState().lead;
            const { lead_treatments: leadTreatment } = followLead ?? {};
            const { practiceId } = store.getState().practiceTreatmentList;
            if (!leadTreatment?.length) {
                dispatch(startLoadingData(false));
                dispatch(updateError({ message: 'Lead data not loaded yet. Please try again.', success: 'warning' }));
                return;
            }
            dispatch(startLoadingData(true));
            dispatch(setLoader(true));
            localStorageRemoveItem('localTreatmentData');
            const treatment = getCurrentTreatmentData(leadTreatment[0]);
            if (store.getState().lead.action === 'startTreatment') {
                const selectedId = store.getState().multiTreatment?.currentTreatmentDataGlobal?.treatmentData?.id
                    || backupLeadTreatment?.id || store.getState().lead.tableRowLead?.treatment_id;
                const selected = leadTreatment.find((row) => String(row.id) === String(selectedId));
                if (!selected) throw new Error('Select the exact treatment before starting.');
                const updated = newLeadDetail.lead_treatment || {};
                const preparationData = {};
                if (updated.custom_price != null) preparationData.custom_price = updated.custom_price;
                if (updated.teethAmount != null) preparationData.leadtreatment_specification = { teeth_count: updated.teethAmount };
                const preparation = Object.keys(preparationData).length ? {
                    url: `/api/v1/practices/${practiceId}/leads/${followLead.id}/treatments/${selected.id}/`, method: 'PATCH', data: preparationData,
                } : undefined;
                await dispatch(StartingTreatment({ lead: followLead.id, id: selected.id }, practiceId, async () => {
                    dispatch(leadActions.setAction(null));
                    if (Array.isArray(archiveLead?.leadAssignees) && archiveLead.leadAssignees.filter(Boolean).length) {
                        await dispatch(updateAssignToUser({ assignees: archiveLead.leadAssignees.filter(Boolean) }, followLead.id, practiceId, () => {}));
                    }
                    if (String(store.getState().practiceTreatmentList?.practiceId) !== String(practiceId)) return;
                    // Capture has resolved the consultation already. Only an explicit
                    // future TREATMENT visit is a separate requested booking.
                    if (isSeparatelyRequestedFutureTreatmentBooking(leadAppoiment)) {
                        await dispatch(addAppointment({ ...leadAppoiment, lead_treatment: selected.id }, practiceId, followLead.id, handleClose, 'followUp'));
                    } else handleClose?.('finish', followLead.id);
                }, 'startingTreatmentFinish', { preparation }));
                return;
            }
            const { leadAssignees } = archiveLead ?? {};
            if (Array.isArray(leadAssignees) && leadAssignees.length) {
                try {
                    const ids = leadAssignees.filter(Boolean);
                    const targetLeadId = (treatment && treatment.lead) || followLead?.id;
                    if (targetLeadId && ids.length > 0) {
                        dispatch(updateAssignToUser({ assignees: ids }, targetLeadId, practiceId, () => { }));
                    }
                } catch (e) { /* noop */ }
            }
            if (treatment) {
                if (data === 'Unable to contact') {
                    dispatch(leadActions.saveTreatment(treatment));
                    dispatch(unableToContact(handleClose, isNoNotes));
                    return;
                }
                if (activeTab >= 1 && archiveLead && archiveLead.notes) {
                    const archiveNote = {
                        ...archiveLead.notes,
                        lead: treatment?.lead || followLead?.id,
                        treatment: treatment?.id
                    };

                    if (archiveNote.note === "NHS_WAITING_LIST") {
                        dispatch(addNotes(archiveNote, practiceId, handleClose, 'nhs'));
                        return;
                    }

                    dispatch(addNotes(archiveNote, practiceId, handleClose, 'archive'));
                    return;
                }
                const updatedTreatment = newLeadDetail && newLeadDetail.lead_treatment && newLeadDetail.lead_treatment || {};
                dispatch(leadActions.saveTreatment(treatment));
                // eslint-disable-next-line eqeqeq
                if (!isRecursiveCall && (treatment?.treatment !== updatedTreatment.treatment || treatment?.price != updatedTreatment?.custom_price || treatment?.leadtreatment_specification?.teeth_count != updatedTreatment.teethAmount)) {
                    const treatmentDetail = {
                        ...newLeadDetail.lead_treatment,
                        lead: treatment?.lead || followLead?.id,
                        id: treatment?.id
                    };
                    if (treatmentDetail?.id)
                        dispatch(UpdateTreatmentV2(treatmentDetail, practiceId, handleClose, 'updateLeadDetail'));
                    return;
                }
                dispatch(addAppointment({ ...leadAppoiment, lead_treatment: treatment?.id, lead: treatment?.lead || followLead?.id, }, practiceId, treatment?.lead || followLead?.id, handleClose, 'followUp'));
            } else if (!isRecursiveCall) {
                dispatch(addTreatment({ ...backupLeadTreatment, is_practice_notification_required: false }, practiceId, followLead?.id, () => dispatch(updateLeadDetail(data, handleClose, isNoNotes, true)), { needToUpdate: backupLeadTreatment?.teethAmount || backupLeadTreatment?.teethAmount === 0, teeth: backupLeadTreatment?.teethAmount || 0 }, true));
            } else {
                dispatch(updateError({ message: "No Treatment found for lead", success: "warning" }));
                dispatch(setLoader(false));
                dispatch(startLoadingData(false));
                if (activeTab >= 1 && archiveLead && archiveLead.notes) {
                    const archiveNote = {
                        ...archiveLead.notes,
                        lead: followLead?.id
                    };

                    if (archiveNote.note === "NHS_WAITING_LIST") {
                        dispatch(addNotes(archiveNote, practiceId, handleClose, 'nhs'));
                        return;
                    }

                    dispatch(addNotes(archiveNote, practiceId, handleClose, 'archive'));
                    return;
                }
            }
        } catch (error) {
            dispatch(setLoader(false));
            dispatch(updateError({ message: error.message, success: false, cause: error }));
            dispatch(startLoadingData(false));
        }
    };
}

export function addNewLead(data, practiceId, treatmentDetail, formData, handleSuccess, type) {
    return async () => {
        try {
            const payload = mergeAdAttributionIntoLeadPayload(buildManualLeadPayload({
                ...data,
                suppress_initial_message_sequence: true,
                suppress_practice_notification: true,
            }, treatmentDetail));
            const { response, cancelled } = await postManualLead(practiceId, payload, { timeout: 30000, strictPhone: true });
            if (cancelled) {
                dispatch(updateCompleteStatus(false));
                dispatch(startLoadingData(false));
                return;
            }
            if (response?.status === 201) {
                reportDentallyLink(response?.data);
                // Clear stashed ad attribution on every successful lead
                // create so subsequent leads don't inherit the same gclid.
                clearStoredAdAttribution();
                dispatch(leadActions.saveLead(response?.data));
                const createdTreatment = getCreatedManualLeadTreatment(response?.data, treatmentDetail);
                dispatch(updateError({ success: true, message: MANUAL_LEAD_FULLY_SILENT_MESSAGE }));
                const finish = () => handleSuccess({ success: true, leadId: response?.data?.id, formData, event: type });
                if (createdTreatment) {
                    await dispatch(continueCreatedTreatment(createdTreatment, treatmentDetail, practiceId, response?.data?.id, finish, { needToUpdate: treatmentDetail?.teethAmount || treatmentDetail?.teethAmount === 0, teeth: treatmentDetail?.teethAmount || 0 }));
                } else {
                    finish();
                    dispatch(startLoadingData(false));
                }
            } else {
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({ message: friendlyCreateLeadError(response), success: false }));
                dispatch(startLoadingData(false));
            }
        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
            dispatch(startLoadingData(false));
        }
    };
}

// Quick lead creation for call scripts - creates a minimal lead and returns it via callback
export function quickCreateLead(practiceId, leadData, onSuccess, onError) {
    return async () => {
        try {
            const payload = mergeAdAttributionIntoLeadPayload(buildManualLeadPayload({
                ...leadData,
                suppress_initial_message_sequence: true,
                suppress_practice_notification: true,
            }));
            const { response, cancelled, choice } = await postManualLead(practiceId, payload, { strictPhone: true });
            if (cancelled) {
                // Not an error: the operator chose another lead (or cancelled) in the identity dialog.
                // `leadId` = the existing lead they opened or restored, if any.
                const leadId = choice?.restoredLeadId || choice?.openedLeadId || null;
                onError?.(leadId ? { cancelled: true, leadId } : { cancelled: true });
                return;
            }
            if (response?.status === 201) {
                reportDentallyLink(response?.data);
                // See savePersonalDetails — clear on success.
                clearStoredAdAttribution();
                payload.lead_treatments?.forEach((detail) => getCreatedManualLeadTreatment(response?.data, detail));
                dispatch(updateError({ success: true, message: MANUAL_LEAD_FULLY_SILENT_MESSAGE }));
                onSuccess?.(response.data);
            } else {
                dispatch(updateError({ message: friendlyCreateLeadError(response), success: false }));
                onError?.(response.data);
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
            onError?.(error);
        }
    };
}

// ── Notes ────────────────────────────────────────────────────

export function addNotes(data, practiceId, handleClose, status, followLead) {
    return async () => {
        try {
            // Snapshot the current count BEFORE the API call so we can detect table moves
            const leadState = store.getState().lead;
            const tableMove = data?.contacted_via
                ? checkFollowUpTableMove(leadState, data?.treatment)
                : null;

            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data?.lead}/notes/`, 'post', data);

            if (response?.status === 201) {
                // Note: we intentionally do NOT dispatch setFollowUpTableMoved here.
                // The iOS toast it triggers is redundant — the user already knows they added a note.

                const { newLeadDetail, createLead, tableRowLead, newLeadDetail: { archiveLead } } = store.getState().lead;

                if (status === "createLead") return

                if (status === 'UNABLE_TO_CONTACT') {
                    dispatch(updateError({ message: 'Note added successfully', success: true }));
                    dispatch(updateCompleteStatus(false));
                    dispatch(startLoadingData(false));
                    handleClose();
                    return;
                }
                if (status === 'unable') {
                    if (newLeadDetail.addReminder) {
                        const reminderDetail = {
                            ...newLeadDetail.addReminder,
                            lead_treatment: getCurrentTreatmentIdV2(createLead.lead_treatment ? createLead.lead_treatment.id : 0),
                        };
                        dispatch(addReminder(reminderDetail, practiceId, handleClose, 'unable'));
                        return;
                    }
                    dispatch(updateError({ message: 'Note added successfully', success: true }));
                    dispatch(updateCompleteStatus(false));
                    handleClose('finish', data?.lead);
                    dispatch(startLoadingData(false));
                    return;
                }
                if (status === 'attended') {
                    dispatch(updateCompleteStatus(false));
                    handleClose('finish', data?.lead);
                    dispatch(startLoadingData(false));
                    return;
                }
                if (status === 'archive' && newLeadDetail.archiveLead?.callReminder && newLeadDetail.archiveLead?.customReminder === 'no-reminder') {
                    dispatch(updateError({ message: 'Archive scheduled — lead will be removed shortly', success: true }));
                    dispatch(updateCompleteStatus(false));
                    handleClose('finish', data?.lead);
                    dispatch(startLoadingData(false));
                    return;
                }
                if (status === 'archive' && newLeadDetail.archiveLead?.callReminder) {
                    const addReminderObj = {
                        ...newLeadDetail.archiveLead?.addReminderObj,
                        lead: tableRowLead.lead_id || followLead.id,
                        lead_treatment: getCurrentTreatmentIdV2(tableRowLead?.treatment_id || followLead?.lead_treatments[0]?.id)
                    };
                    dispatch(addReminder(addReminderObj, practiceId, handleClose));
                    return;
                }
                if (status === 'leadNote') {
                    if (handleClose) {
                        handleClose('finish', data?.lead);
                    }
                    dispatch(leadActions.addLeadNotes(response?.data?.results || response?.data));;
                    dispatch(updateError({ message: 'Note added successfully', success: true }));
                    dispatch(updateCompleteStatus(false));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (status === 'leadNotePopup') {
                    if (handleClose) {
                        handleClose('finish', data?.lead);
                    }
                    dispatch(updateError({ message: 'Note added successfully', success: true }));
                    dispatch(updateCompleteStatus(false));
                    dispatch(startLoadingData(false));
                    return;
                }
                if (status === 'callScript') {
                    // Silent save for call script auto-save - pass response data to callback
                    if (handleClose) {
                        handleClose({ success: true, status: 201, data: response.data, id: response.data?.id });
                    }
                    return;
                }
                if (status === 'nhs') {
                    dispatch(updateError({ message: 'Add to the NHS waiting list successfully', success: true }));
                    dispatch(updateCompleteStatus(false));
                    handleClose('finish', data?.lead);
                    dispatch(startLoadingData(false));
                    return
                }
                // Safety check: only proceed with archive flow if archiveLead exists
                if (archiveLead?.openCommunication) {
                    if (status === 'archive') {
                        dispatch(updateError({ message: 'Archive scheduled — lead will be removed shortly', success: true }));
                    }
                    const archivedData = { ...archiveLead.openCommunication, id: (archiveLead.openCommunication.id ? archiveLead.openCommunication.id : createLead.id) };
                    dispatch(updatePersonalDetail(archivedData, practiceId, handleClose, 'archived'));
                }
                return;
            }
            dispatch(updateCompleteStatus(false));
            const errorStr = JSON.stringify(response.data || 'Something went wrong');
            if (errorStr.toLowerCase().includes('object does not exist')) {
                dispatch(leadActions.setLeadDetailNotFound(true));
            }
            dispatch(updateError({ message: errorStr, success: false }));
            dispatch(startLoadingData(false));
            dispatch(setLoader(false));
        } catch (error) {
            dispatch(updateCompleteStatus(false));
            const errorStr = JSON.stringify(error.message || 'Something went wrong');
            if (errorStr.toLowerCase().includes('object does not exist')) {
                dispatch(leadActions.setLeadDetailNotFound(true));
            }
            dispatch(updateError({ message: errorStr, success: false, cause: error }));
            dispatch(startLoadingData(false));
            dispatch(setLoader(false));
        }
    };
}

export function updateNotes(data, practiceId, noteId, handleCallback, status) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data?.lead}/notes/${noteId}/`, 'patch', data);

            if (response?.status === 200) {
                if (status === 'callScript') {
                    // Silent update for call script auto-save
                    if (handleCallback) {
                        handleCallback({ success: true, data: response.data });
                    }
                    return;
                }
                dispatch(updateError({ message: 'Note updated successfully', success: true }));
                if (handleCallback) {
                    handleCallback({ success: true, data: response.data });
                }
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
                if (handleCallback) {
                    handleCallback({ success: false, error: response.data });
                }
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
            if (handleCallback) {
                handleCallback({ success: false, error: error.message });
            }
        }
    };
}

export function getLeadNotes(id, practiceId, handleNext) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${id}/notes/?fields={additional_notes,contacted_via,id,lead,note,treatment,created_by,created_at,updated_at}&ordering=-updated_at`);
            if (response?.status === 200) {
                dispatch(leadActions.leadNote(response.data.results || response.data));;
            } else {
                // Settle the lead-detail loader gate (leadNotes null => permanent blur).
                dispatch(leadActions.leadNote([]));
                errorHandler(response.data, handleNext, leadActions.hasError);
            }
        } catch (error) {
            dispatch(leadActions.leadNote([]));
            errorHandler(error, handleNext, leadActions.hasError);
        }
    };
}

export function getLeadNotesWithFilter(id, practiceId, handleNext) {
    return async () => {
        try {
            const notes = ['AWAITING_RESPONSE', 'CHANGED_MIND', 'CREDIT_CHECK_FAILED', 'FOLLOW_UP', 'OTHER', 'TOO_EXPENSIVE'];
            const notesQueryParam = encodeURIComponent(notes.join(','));
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${id}/notes/?fields={}&note__in=${notesQueryParam}`);
            if (response?.status === 200) {
                dispatch(leadActions.leadNoteWithFilter(response.data));;
            } else {
                errorHandler(response.data, handleNext, leadActions.hasError);
            }
        } catch (error) {
            errorHandler(error, handleNext, leadActions.hasError);
        }
    };
}

// ── Search ───────────────────────────────────────────────────

export function searchLead(data, practiceId, type, status, treatmentId) {
    return async () => {
        try {
            dispatch(leadActions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/search/?query=${data}${type === 'callScriptPreview' ? '&lead_treatments__status=NEW&is_archived=false' : type === 'FinancialTracker' ? `&is_archived=false&lead_treatments__isnull=false${status ? `&lead_treatments__status__in=${status}` : ''}` : type === 'taskLead' ? '&is_archived=false' : type === 'AlignerApprove' ? `&is_archived=false&lead_treatments__isnull=false&lead_treatments__status=TREATMENT_STARTED&lead_treatments__treatment__in=${treatmentId}&lead_treatment_attributes__contains=${JSON.stringify([{ value: 'Arrived' }])}` : type === 'createLeadSearch' ? '&include_archived=true' : ''}${type === 'ReviewLink' ? '&is_archived=false&lead_treatments__isnull=false' : ''}&${leadTableFields}`);
            if (response?.status === 200) {
                switch (type) {
                    case 'createLeadSearch':
                    case 'AlignerApprove':
                    case 'ReviewLink':
                    case 'FinancialTracker':
                    case 'taskLead':
                    case 'callScriptPreview':
                        dispatch(leadActions.addTaskSearchLead(response.data.results));
                        break;
                    default:
                        dispatch(leadActions.searchAPIdata(response.data.results));
                        break;
                }
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data || 'Something went wrong'), success: false }));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error.message));
        }
    };
}

// Universal search across all entity types (leads, treatments, appointments, invoices, conversations, notes, staff)
// Falls back to existing lead search if universal endpoint is not yet deployed
export function universalSearch(query, practiceId, category = null) {
    return async () => {
        try {
            dispatch(leadActions.startLoading());
            const categoryParam = category ? `&category=${category}` : '';
            const response = await callApi(`/api/v1/practices/${practiceId}/search/?query=${encodeURIComponent(query)}${categoryParam}`);
            if (response?.status === 200) {
                dispatch(leadActions.setUniversalSearchResults(response.data));
            } else if (response?.status === 404) {
                // Universal endpoint not deployed yet — fall back to lead search
                const fallback = await callApi(`/api/v1/practices/${practiceId}/leads/search/?query=${encodeURIComponent(query)}&${leadTableFields}`);
                if (fallback?.status === 200) {
                    dispatch(leadActions.searchAPIdata(fallback.data.results));
                    dispatch(leadActions.setUniversalSearchResults(null));
                }
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data || 'Something went wrong'), success: false }));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error.message));
        }
    };
}

export function searchCampaigns(query, practiceId) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/campaigns/?search=${encodeURIComponent(query)}&ordering=-created_at&limit=5`
            );
            if (response.ok && response.data?.results) {
                dispatch(leadActions.mergeCampaignSearchResults(response.data.results));
            }
        } catch (e) {
            // silent fail — campaign search is supplementary
        }
    };
}

// ── Archive ──────────────────────────────────────────────────

export function archivedLeadAPI(data, practiceId, handleClose, id) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${id}/archive/`, 'post', data);
            if (response?.status === 200) {
                dispatch(updateCompleteStatus(false));
                dispatch(updateError({ message: 'Lead will archived in 5 minutes.', success: true }));
                handleClose('finish', true);
                dispatch(startLoadingData(false));
                return;
            }
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(response.data) || 'Something went wrong', success: false }));
            dispatch(startLoadingData(false));
        } catch (error) {
            dispatch(updateCompleteStatus(false));
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
            dispatch(startLoadingData(false));
        }
    };
}

export function archivedLead({ practiceId, offset, limit, treatment, searchQuery, tags, referral, startDate, endDate, order, orderId, archiveReason, noTreatment }) {
    return async () => {
        try {
            const orderingLead = order === 'desc' ? `&ordering=-${orderId}` : `&ordering=${orderId}`;
            const treatmentQuery = noTreatment ? '&lead_treatments__isnull=true' : treatment ? `&lead_treatments__treatment__in=${treatment}` : '';
            const archiveSearchQuery = searchQuery ? `&search_query=${searchQuery}` : '';
            const archivedTags = tags ? `&tagged_items__tag__name__in=${tags}` : '';
            const archiveReferral = referral ? `&lead_source__referral__in=${referral}` : '';
            // Archive page semantics (2026-07-05): the date filter means "archived during
            // this period" — filter on archived_at, not lead creation date.
            const archiveDate = (startDate && endDate) ? `&archived_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}` : '';
            const reason = archiveReason ? `&archived_reason__in=${archiveReason}` : '';
            const response = await callApi(`/api/v1/practices/${practiceId}/archived/?limit=${limit}&offset=${offset}${orderingLead}${treatmentQuery}${archiveSearchQuery}${archivedTags}${archiveReferral}${archiveDate}&is_archived=true${reason}`);
            if (response?.status === 200) {
                dispatch(archiveLead(response.data));
                dispatch(startLoadingData(false));
            } else {
                dispatch(leadActions.hasError(response.data.message || 'Something went wrong'));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error.data.message));
        }
    };
}

export function restoreArchived(handleClose) {
    return async () => {
        try {
            const { newLeadDetail: { archiveLead }, tableRowLead } = store.getState().lead;
            const { practiceId } = store.getState().practiceTreatmentList;
            // archiveLead is populated when restore is triggered from the lead table (Archive
            // dialog sets it via saveNewLeadDetail). On the standalone ArchiveDetailView page
            // it may be absent — fall back to an empty body so the POST still fires correctly
            // (the restore endpoint only requires the lead ID in the URL).
            const restorePayload = archiveLead?.archiveData ?? {};
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${tableRowLead.lead_id}/restore/`, 'post', restorePayload);
            if (response?.status === 200) {
                // Was 'Lead will restore in 5 minutes.' — which is simply untrue.
                // apps/leads/views.py::restore is a synchronous serializer.save()
                // with no delay, apply_async or countdown, so the lead is already
                // back before this message renders. Verified on staging: three
                // restores, all is_archived=false and archived_at=null immediately.
                // ARCHIVE genuinely IS deferred, which is where the wording came
                // from; it was copied onto restore without checking. A user reading
                // it either waits five minutes for finished work or assumes failure.
                dispatch(updateError({ message: 'Lead restored.', success: true }));
                dispatch(updateCompleteStatus(false));
                handleClose('finish');
                dispatch(startLoadingData(false));
            } else {
                dispatch(updateCompleteStatus(false));
                // extractApiError understands DRF's detail / message /
                // non_field_errors / per-field shapes. JSON.stringify put the raw
                // response in the snackbar, and errorHandler then collapsed
                // anything starting with { to "Something went wrong", destroying
                // the per-field reasons. `status` matters too: without it
                // errorHandler treats a 4xx as a network error and ships it to
                // Sentry, defeating its own documented 4xx-no-capture gate.
                dispatch(updateError({ message: extractApiError(response), success: false, status: response?.status }));
                dispatch(startLoadingData(false));
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false, cause: error }));
            dispatch(updateCompleteStatus(false));
            dispatch(startLoadingData(false));
        }
    };
}

// In-flight guard for the scoped restore helper — one entry per leadId so a
// rapid double-click cannot fire duplicate restore POSTs (mirrors the
// _archiveInFlight pattern used by beginArchiveLead).
const _restoreInFlight = new Set();

/**
 * Scoped restore helper for the "Create Lead" flow.
 * Does NOT touch Archive-table Redux state — just POSTs to the restore endpoint.
 *
 * The BE restore endpoint is ASYNC: it returns 200 `{ scheduled: true }` and the
 * lead reappears in active leads a few minutes later. This helper therefore only
 * returns the raw response; the caller shows a "scheduled" toast and does NOT
 * navigate to the (not-yet-restored) lead detail.
 *
 * Single-flight: a second call for the same leadId while one is in-flight is a
 * no-op that resolves to `{ __inFlight: true }`, so idempotency holds at the
 * helper layer regardless of the button's disabled state.
 */
export async function restoreArchivedLead(practiceId, leadId) {
    const key = String(leadId);
    if (_restoreInFlight.has(key)) return { __inFlight: true };
    _restoreInFlight.add(key);
    try {
        const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/restore/`, 'post', {});
        return response;
    } finally {
        _restoreInFlight.delete(key);
    }
}

export function ArchivedLeadDashBoard(handleClose, followLead) {
    return async () => {
        try {
            const { newLeadDetail, tableRowLead } = store.getState().lead;
            const { practiceId } = store.getState().practiceTreatmentList;
            const treatmentId =
                tableRowLead?.treatment_id ||
                tableRowLead?.lead_treatments?.[0]?.id ||
                followLead?.lead_treatments?.[0]?.id ||
                '';
            const treatment = treatmentId !== undefined ? { treatment: treatmentId } : {};
            const archiveNote = {
                ...newLeadDetail.archiveLead.notes,
                lead: tableRowLead?.lead_id || followLead?.id,
                ...treatment,
            };
            dispatch(addNotes(archiveNote, practiceId, handleClose, 'archive', followLead));
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false, cause: error }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

// ── beginArchiveLead ────────────────────────────────────────────────────────
// In-flight guard — one entry per leadId so a rapid double-submit is a no-op.
const _archiveInFlight = new Set();

/**
 * beginArchiveLead — canonical archive command.
 *
 * Direct, sentinel-free path that owns the full lifecycle:
 *   1. Validate  — bail with error toast if leadId or practiceId missing
 *   2. Guard     — silent no-op if an archive for this leadId is already in-flight
 *   3. Note POST — /practices/{p}/leads/{l}/notes/
 *   4. Comm PATCH — /practices/{p}/leads/{l}/  (is_open_for_communication) — non-blocking
 *   5. Archive   — reuses archivedLeadAPI (single POST, no duplication)
 */
export function beginArchiveLead({ leadId, practiceId, leadTreatmentId, reason, note, isOpenForCommunication, source, handleClose }) {
    return async () => {
        if (!leadId || !practiceId) {
            dispatch(updateError({ message: '[beginArchiveLead] Archive aborted: leadId or practiceId missing', success: false }));
            return;
        }

        const key = String(leadId);
        if (_archiveInFlight.has(key)) return;
        _archiveInFlight.add(key);

        try {
            dispatch(startLoadingData(true));

            const notePayload = {
                note: reason || '',
                additional_notes: note || '',
                lead: leadId,
                ...(leadTreatmentId ? { treatment: leadTreatmentId } : {}),
            };
            const noteRes = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/notes/`,
                'post',
                notePayload
            );
            if (!noteRes?.ok) {
                dispatch(updateError({
                    message: `Archive failed: could not save note (status ${noteRes?.status ?? 'network error'})`,
                    success: false,
                }));
                dispatch(startLoadingData(false));
                return;
            }

            if (typeof isOpenForCommunication === 'boolean') {
                const patchRes = await callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/`,
                    'patch',
                    { id: leadId, is_open_for_communication: isOpenForCommunication }
                );
                if (!patchRes?.ok) {
                    // eslint-disable-next-line no-console
                    console.warn('[beginArchiveLead] is_open_for_communication PATCH failed', patchRes?.status, { source });
                }
            }

            await dispatch(archivedLeadAPI({ note: note || '' }, practiceId, handleClose, leadId));
        } catch (error) {
            dispatch(updateError({
                message: String(error?.message || 'Something went wrong'),
                success: false,
                cause: error,
            }));
            dispatch(startLoadingData(false));
        } finally {
            _archiveInFlight.delete(key);
        }
    };
}

// ── Assign ───────────────────────────────────────────────────

export function UpdateAssignMember(data, practiceId, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${data?.lead}/appointments/${data.id}/`, 'patch', withAssigneeIds(data));
            if (response?.status === 200) {
                dispatch(getAppointment(practiceId, data?.lead));
                handleSuccess({ success: true, message: 'Appointment practitioner updated successfully' });
            }
        } catch (error) {
            const message = error?.message || 'An error occurred while updating the practitioner';
            handleSuccess({ message, success: false });
            dispatch(leadActions.hasError(message));
        }
    };
}

// ── Bulk operations ──────────────────────────────────────────

/**
 * Group lead ids by the practice that owns each lead. Ids without a known
 * practice fall back to `fallbackPracticeId`.
 *
 * @param {Array<number>} ids
 * @param {Object<string, number|string>|null|undefined} practiceByLeadId lead id -> practice id
 * @param {number|string} fallbackPracticeId
 * @returns {Map<string, number[]>} practice id (string) -> lead ids
 */
// Backend rejects bulk_archive_restore requests with more object_ids than this.
export const BULK_ARCHIVE_RESTORE_MAX_IDS = 200;

export function groupLeadIdsByPractice(ids, practiceByLeadId, fallbackPracticeId) {
    const groups = new Map();
    (ids || []).forEach((id) => {
        const own = practiceByLeadId?.[id] ?? practiceByLeadId?.[String(id)];
        const key = String(own ?? fallbackPracticeId);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(id);
    });
    return groups;
}

/**
 * Bulk archive / restore.
 *
 * In "All Practices" mode the selection can span practices, and the backend
 * requires every id to belong to the practice in the URL. `practiceByLeadId`
 * (optional lead id -> practice id map) lets us send one call per practice.
 * Without it (or with a single practice) this is exactly one call to
 * `practiceId`, as before.
 */
export function bulkArchivedOrRestore(practiceId, data, handleSuccess, practiceByLeadId) {
    return async () => {
        try {
            if (data?.object_ids) {
                data.object_ids = data.object_ids.map(Number).filter(id => Number.isInteger(id) && id > 0);
            }
            const groups = groupLeadIdsByPractice(data?.object_ids, practiceByLeadId, practiceId);
            const isArchive = data?.request_type === 'ARCHIVE';
            const successMessage = isArchive ? 'Lead will archived in 5 minutes.' : 'Restore completed successfully.';

            // One request per (practice, chunk of at most BULK_ARCHIVE_RESTORE_MAX_IDS).
            const chunks = [];
            groups.forEach((ids, groupPractice) => {
                for (let i = 0; i < ids.length; i += BULK_ARCHIVE_RESTORE_MAX_IDS) {
                    chunks.push([groupPractice, ids.slice(i, i + BULK_ARCHIVE_RESTORE_MAX_IDS)]);
                }
            });

            if (chunks.length <= 1) {
                // Single practice (or nothing selected): unchanged behaviour. The URL
                // practice is the group's practice when known, else the passed one.
                const targetPractice = groups.size === 1 ? [...groups.keys()][0] : practiceId;
                const response = await callApi(`/api/v1/practices/${targetPractice}/leads/bulk_archive_restore/`, 'post', data);
                if (response?.status === 200) {
                    handleSuccess('finish');
                    dispatch(updateError({ message: successMessage, success: true }));
                } else {
                    // Was JSON.stringify(response?.data) — that put the raw serializer
                    // response into the snackbar. extractApiError understands DRF's
                    // shapes (detail / message / non_field_errors / per-field arrays).
                    dispatch(updateError({ message: extractApiError(response), success: false, status: response?.status }));
                    // Report 'error', NOT 'finish'. Callers already gate their dialog
                    // close on 'finish' (see FloatingBulkActionBar) — sending 'finish'
                    // on a failure closed the dialog over a failed action.
                    handleSuccess('error', { succeededIds: [] });
                }
                return;
            }

            // Several requests (multiple practices and/or more than the backend
            // per-request cap): sequential. A failing chunk never stops the rest.
            const succeededIds = [];
            let failedChunks = 0;
            let failedLeads = 0;
            await chunks.reduce((chain, [chunkPractice, chunkIds]) => chain.then(async () => {
                try {
                    const response = await callApi(
                        `/api/v1/practices/${chunkPractice}/leads/bulk_archive_restore/`,
                        'post',
                        { ...data, object_ids: chunkIds }
                    );
                    if (response?.status === 200) {
                        succeededIds.push(...chunkIds);
                    } else {
                        failedChunks += 1;
                        failedLeads += chunkIds.length;
                    }
                } catch {
                    failedChunks += 1;
                    failedLeads += chunkIds.length;
                }
            }), Promise.resolve());
            if (failedChunks === 0) {
                handleSuccess('finish');
                dispatch(updateError({ message: successMessage, success: true }));
            } else {
                const unit = chunks.length === groups.size ? 'practices' : 'batches';
                dispatch(updateError({
                    message: `${isArchive ? 'Archive' : 'Restore'} failed for ${failedChunks} of ${chunks.length} ${unit} (${failedLeads} of ${data.object_ids.length} leads not processed). The other leads were processed.`,
                    success: false,
                }));
                handleSuccess('error', { succeededIds });
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Something went wrong', success: false, cause: error }));
            handleSuccess('error', { succeededIds: [] });
        }
    };
}

export function bulkUpdate(practiceId, data) {
    return async () => {
        try {
            if (data?.object_ids) {
                data.object_ids = data.object_ids.map(Number).filter(id => Number.isInteger(id) && id > 0);
            }
            if (data?.ids) {
                data.ids = data.ids.map(Number).filter(id => Number.isInteger(id) && id > 0);
            }
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/bulk_update/?is_archived_included=true`, 'post', data);
            if (response?.status === 200) {
                dispatch(updateCompleteStatus(false));
                dispatch(startLoadingData(false));
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data), success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
        }
    };
}

// ── Misc ─────────────────────────────────────────────────────

export function getMergeTags() {
    return async () => {
        try {
            const response = await callApi('/api/v1/utils/mergetags/');
            if (response?.status === 200) {
                dispatch(leadActions.setMergeTags(response?.data));
                localStorage.setItem('MergeTagsData', JSON.stringify(response?.data));
            } else {
                dispatch(updateError({ message: JSON.stringify(response?.data), success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
        }
    };
}

export function UnsubscribeLead(practiceId, data, callback) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/unsubscribe_email/`, 'post', data);
            if (response?.status === 200) {
                callback({ success: true, message: 'Lead unsubscribed successfully' });
            } else {
                callback({ success: false, message: JSON.stringify(response?.data) || 'Something went wrong' });
            }
        } catch (error) {
            dispatch(leadActions.hasError(error.message));
        }
    };
}

export function ViewLeadsForExistingAudience(practiceId, fieldParams, filter, search) {
    return async () => {
        try {
            const limit = fieldParams?.limit ? `&limit=${fieldParams?.limit}` : '&limit=10';
            const offset = (fieldParams?.offset === 0 || fieldParams?.offset) ? `&offset=${fieldParams?.offset}` : '&offset=0';
            const param = `${limit}${offset}`;
            const additionalFilter = filter
                ? Object.entries(filter)
                    .map(([key, value]) => {
                        if (key === 'created_at__range') {
                            const [startDate, endDate] = value;
                            return `created_at__range=${`${startDate} 00:00:00`},${`${endDate} 23:59:00`}`;
                        }
                        return `${key}=${value}`;
                    })
                    .join('&')
                : '';

            const baseApi = search ? 'leads/search' : 'leads'

            const response = await callApi(`/api/v1/practices/${practiceId}/${baseApi}/?fields={created_at,created_at_human,dob,first_name,id,last_name,lead_contact,salutation,is_unsubscribed,contacted_count,updated_at,updated_at_human,is_archive_pending,is_archived,tags}&ordering=-created_at${param}&is_archived=false${additionalFilter ? `&${additionalFilter}` : ''}${search ? `&query=${search}` : ''}`);
            if (response.status === 200) {
                dispatch(leadActions.updateExistingAudienceList(response.data));
                dispatch(startLoadingData(false));

            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
                dispatch(startLoadingData(false));

            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false, cause: error }));
            dispatch(startLoadingData(false));
        }
    };
}

// Reminder time for the transfer. The server reads this string as Europe/London
// wall-clock (the "fake Z" convention), so it must be built in London time and never
// from the browser's local clock.
export const buildLondonReminderDatetime = (hoursAhead = 2, now = new Date()) =>
    `${dayjs(now).add(hoursAhead, 'hour').tz('Europe/London').format('DD-MM-YYYY HH:mm:ss')}.0Z`;

const TRANSFER_PROGRESS_TOAST_IDS = ['transfer-progress-1', 'transfer-progress-2'];
const TRANSFER_OUTCOME_TOAST_ID = 'transfer-outcome';
// The final result must stay readable after the dialog closes / the table reloads.
const TRANSFER_TOAST_MS = { success: 6000, warning: 10000, error: 12000 };

export function archiveFromCurrentPracticeAndMove(currentPracticeId, nextPracticeId, nextPracticeName, currentPracticeName, leadDetails, handleSuccess) {
    return async () => {
        // handleSuccess("finish") fires in `finally` on success AND failure; `archived`
        // (2nd arg) lets callers tell them apart — true only once the original lead's
        // archive request was accepted.
        let archived = false;
        // Progress toasts are cleared by the final outcome toast, which has its own id (see
        // ErrorHandler) - otherwise notistack removes the outcome together with the progress
        // toasts (same id) and the user never sees the result. Each progress step has its own
        // id too: two live snackbars must never share one.
        const progress = (step, message) => dispatch(updateError({
            message, success: "warning", toastId: TRANSFER_PROGRESS_TOAST_IDS[step], dismissToastId: step ? TRANSFER_PROGRESS_TOAST_IDS[step - 1] : undefined,
        }));
        // `viewLead` ({ lead, leadPracticeId }) adds a "View lead" button for the copy in the
        // destination practice (ErrorHandler navigates, so it works after the dialog is gone).
        const outcome = (message, success, timeout, viewLead) => dispatch(updateError({
            message, success, timeout, toastId: TRANSFER_OUTCOME_TOAST_ID, dismissToastId: TRANSFER_PROGRESS_TOAST_IDS,
            ...(viewLead && { ...viewLead, leadActionLabel: 'View lead' }),
        }));
        try {
            progress(0, 'Moving Lead to New Practice');
            const original = leadDetails?.originalData;
            const dataPayload = {
                "salutation": original?.salutation,
                "first_name": original?.first_name,
                "dob": original?.dob,
                "is_registered_patient": original?.is_registered_patient,
                "lead_contact": {
                    "phone": original?.lead_contact?.phone,
                    "email": original?.lead_contact?.email,
                    "preferred_contact_method": original?.lead_contact?.preferred_contact_method
                },
                "lead_source": {
                    "type": original?.lead_source?.type,
                    "referral": original?.lead_source?.referral
                },
                "last_name": original?.last_name,
                "suppress_initial_message_sequence": true,
                "suppress_practice_notification": true,
                "lead_address": {
                    "address_line_1": original?.lead_address?.address_line_1,
                    "postcode": original?.lead_address?.postcode,
                    "address_line_2": original?.lead_address?.address_line_2,
                    "city": original?.lead_address?.city,
                    "county": original?.lead_address?.county
                },
                "assignees": [],
            };
            // The copy must never be MORE permissive than the source: the backend
            // defaults these to true on create, so copy the source's values whenever known.
            ['email_marketing_consent', 'sms_marketing_consent', 'whatsapp_marketing_consent', 'is_open_for_communication'].forEach((key) => {
                if (typeof original?.[key] === 'boolean') dataPayload[key] = original[key];
            });
            // NOTE: do NOT merge ad attribution here — this is the
            // practice-switcher path that moves an existing lead to a
            // different practice, not a fresh capture. Carrying the
            // current session's gclid would wrongly attribute a
            // lead-move as a new ad conversion. The original lead's
            // lead_source is copied via dataPayload already.
            const createResponse = await callApi(`/api/v1/practices/${nextPracticeId}/leads/`, 'post', dataPayload);
            if (isIdentityConflict(createResponse)) {
                // Nothing was created: abort without inventing an identity_resolution decision.
                outcome(`${nextPracticeName} already has a lead with this patient's phone or email, so it wasn't transferred. This lead is unchanged.`, false, TRANSFER_TOAST_MS.error);
                return;
            }
            if (createResponse?.status !== 201 || !createResponse?.data?.id) {
                outcome(`Could not create the lead in ${nextPracticeName}${createResponse?.status ? ` (HTTP ${createResponse.status})` : ''}. Nothing was changed.`, false, TRANSFER_TOAST_MS.error);
                return;
            }
            const newLeadId = createResponse.data.id;
            const failedSteps = [];
            progress(1, `Lead copied to ${nextPracticeName}. Adding note and reminder...`);
            const noteText = `This lead was moved to ${nextPracticeName} from ${currentPracticeName}`;
            try {
                const noteResponse = await callApi(`/api/v1/practices/${nextPracticeId}/leads/${newLeadId}/notes/`, 'post', { lead: newLeadId, additional_notes: noteText });
                if (noteResponse?.status !== 201) failedSteps.push('note');
            } catch (stepError) {
                failedSteps.push('note');
            }
            try {
                const reminderResponse = await callApi(`/api/v1/practices/${nextPracticeId}/reminders/`, 'post', {
                    method: "CALL",
                    datetime: buildLondonReminderDatetime(2),
                    note: noteText,
                    assignees: [],
                    is_completed: false,
                    group: 'TODO',
                    lead: newLeadId
                });
                if (reminderResponse?.status !== 201) failedSteps.push('reminder');
            } catch (stepError) {
                failedSteps.push('reminder');
            }
            // Archive the original even if the note/reminder failed, so no duplicate active
            // lead remains and a retry cannot create a third copy.
            let archiveResponse;
            try {
                archiveResponse = await callApi(`/api/v1/practices/${currentPracticeId}/leads/bulk_archive_restore/`, 'post', {
                    object_ids: [original?.id],
                    request_type: 'ARCHIVE',
                    reason: `ANOTHER_PRACTICE`
                });
            } catch (archiveError) {
                archiveResponse = null;
            }
            const viewLead = nextPracticeId ? { lead: newLeadId, leadPracticeId: nextPracticeId } : undefined;
            if (archiveResponse?.status === 200) {
                archived = true;
                if (failedSteps.length) {
                    outcome(`Lead copied to ${nextPracticeName}, but the ${failedSteps.join(' and ')} couldn't be added — please add it manually. The original lead is being archived.`, "warning", TRANSFER_TOAST_MS.warning, viewLead);
                } else {
                    outcome(`Lead transferred to ${nextPracticeName}. The original lead is being archived.`, true, TRANSFER_TOAST_MS.success, viewLead);
                }
            } else {
                outcome(`The lead was copied to ${nextPracticeName}, but archiving the original here failed${archiveResponse?.status ? ` (HTTP ${archiveResponse.status})` : ''}. The original is still active — do not transfer it again; archive it manually.${failedSteps.length ? ` The ${failedSteps.join(' and ')} also couldn't be added.` : ''}`, false, TRANSFER_TOAST_MS.error);
            }
        } catch (error) {
            console.error('Lead transfer failed', error?.message);
            outcome(`The lead transfer failed: ${error?.message || 'unexpected error'}. Check ${nextPracticeName} before trying again.`, false, TRANSFER_TOAST_MS.error);
        } finally {
            handleSuccess("finish", { archived })
        }
    }
}

export function conversations(id, practiceId) {
    // eslint-disable-next-line consistent-return
    return async () => {
        if (!hasFeatureAccess('chatbot')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${id}/conversations/`);
            if (response?.status === 200) {
                dispatch(leadActions.chatbotConversationSlice(response.data));
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false, status: response?.status }));
            }
        } catch (error) {
            dispatch(leadActions.hasError(error?.message));
        }
    };
}

export function getLeadSummary(practiceId, startDate, endDate, status) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/reports/lead-treatment-category-summary/${status ? `?status=${status}` : ''}${startDate ? `?time_after=${startDate}&` : ''}${endDate ? `time_before=${endDate}` : ''}`);
            if (response?.status === 200) {
                dispatch(leadActions.leadSummaryListingSlice(response.data));
            } else {
                dispatch(updateError({ success: false, message: response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error?.message || 'Something went wrong', cause: error }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function getLeadStatusSummary(practiceId, startDate, endDate, type) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/reports/lead-status-summary/${startDate ? `?time_after=${startDate}&` : ''}${endDate ? `time_before=${endDate}` : ''}`);
            if (response?.status === 200) {
                if (type === 'ReportSummary') {
                    dispatch(leadActions.leadStatusSummaryListingSlice(response.data));
                }
                dispatch(leadActions.addDashboardStatusesSummery(response.data));
            } else {
                dispatch(updateError({ success: false, message: response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error?.message || 'Something went wrong', cause: error }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function getAutoMatedMessages(practiceId, leadId, handleNext) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/tasks/?fields={created_at,created_at_human,due,due_human,id,is_cancelled,is_enqueued,name,task_status,updated_at,updated_at_human,instance_object}`);
            if (response?.status === 200) {
                dispatch(leadActions.addAutoMatedMessages(response.data));
            } else {
                errorHandler(response.data, handleNext, leadActions.hasError);
            }
        } catch (error) {
            errorHandler(error, handleNext, leadActions.hasError);
        }
    };
}

export function AutoMatedMessageStatusUpdate(practiceId, leadId, id, detail, handleSuccess) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/tasks/${id}/`, 'patch', detail);
            if (response?.status === 200) {
                handleSuccess({ success: true, message: 'Message detail updated successfully' });
                dispatch(getAutoMatedMessages(practiceId, leadId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response?.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error?.message || 'Something went wrong', cause: error }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function getLeadArchivedSummary(practiceId, startDate, endDate) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/reports/archived-lead-summary/${startDate ? `?time_after=${startDate}&` : ''}${endDate ? `time_before=${endDate}` : ''}`);
            if (response?.status === 200) {
                dispatch(leadActions.leadArchivedSummaryListingSlice(response.data));
            } else {
                dispatch(updateError({ success: false, message: response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error?.message || 'Something went wrong', cause: error }));
            dispatch(updateCompleteStatus(false));
        }
    };
}

export function getBookingSuggestions({ practiceId, treatmentId }) {
    return async () => {
        try {
            const params = treatmentId ? `?treatment_id=${treatmentId}` : '';
            const response = await callApi(`/api/v1/practices/${practiceId}/leads/booking_suggestions/${params}`, 'get');
            if (response?.status === 200) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('Failed to fetch booking suggestions:', error);
            return null;
        }
    };
}
